/**
 * App-wide translations. Key by language code (en, es, fr, de, pt, it, nl, ja).
 * Use with LocaleContext: t('home.heroTitle')
 */
import { DEALERSHIP_PHONE_DISPLAY as PHONE } from './constants/dealershipContact.js';

const translations = {
  en: {
    home: {
      heroFindTitle: 'Find your car',
      price: 'Price',
      brand: 'Brand',
      type: 'Type',
      year: 'Year',
      any: 'Any',
      search: 'Search',
      heroTitle: 'Honest cars.',
      heroTitleAccent: 'Straight deals.',
      heroSlide2Title: 'Quality you can trust.',
      heroSlide2Accent: 'Every time.',
      heroSlide3Title: 'Transparent pricing.',
      heroSlide3Accent: 'No surprises.',
      heroTrust1: 'Financing available',
      heroTrust2: 'Full vehicle history',
      heroTrust3: 'Fair trade-in values',
      heroScrollHint: 'Scroll to explore',
      heroSubtitle: 'Quality pre-owned vehicles, transparent pricing, and service you can trust. Discover {{count}}+ cars from {{brands}} brands.',
      browseInventory: 'Browse inventory',
      soundOn: 'Enable background music',
      soundOff: 'Disable background music',
      contactUs: 'Contact us',
      trustedDealer: 'Trusted dealer',
      licensedTransparent: 'Licensed & transparent',
      awardWinningService: 'Trusted service',
      customerFirst: 'Customer-first experience',
      transparentPricing: 'Transparent pricing',
      noHiddenFees: 'No hidden fees',
      responsibleBuying: 'Responsible buying',
      qualityPreOwned: 'Quality pre-owned, inspected',
      newArrivalsThisWeek: 'New arrivals this week',
      vehiclesJustAdded: '{{count}}+ vehicles just added',
      findOutMore: 'Find out more',
      financingAvailable: 'Financing available',
      getPreApproved: 'Get pre-approved in minutes',
      sellOrTrade: 'Sell or trade your car',
      instantValuation: 'Instant valuation, fair pricing',
      whatWeOffer: 'What we offer',
      fromBrowsingToFinancing: 'From browsing to financing, we\'re with you at every step.',
      seeMore: 'See more',
      browseInventoryDesc: 'Explore our selection of quality pre-owned vehicles. Every car is inspected and ready for your next journey.',
      sellTradeTitle: 'Sell / Trade-in',
      sellTradeDesc: 'Get an instant valuation for your vehicle. Upgrade to your next car with a simple, transparent process.',
      financingTitle: 'Financing',
      financingDesc: 'Competitive rates and flexible terms. Pre-approval in minutes so you can drive home with confidence.',
      contactSupport: 'Contact & support',
      contactSupportDesc: 'Visit us, schedule a test drive, or ask anything. We\'re here to help you find the right car.',
      carsAvailable: 'Cars available',
      carsSold: 'Cars sold',
      newArrivals: 'New arrivals',
      brands: 'Brands',
      mostViewedCars: 'Most Viewed Cars in Our Lot',
      shopByType: 'Shop by type',
      popularBrands: 'Popular brands',
      trustedManufacturers: 'Trusted manufacturers in our lineup',
      available: 'available',
      comingSoon: 'Coming soon',
      financingOptions: 'Financing options',
      preApprovalTerms: 'Pre-approval in minutes, competitive rates, flexible terms.',
      quickApproval: 'Quick approval',
      competitiveRates: 'Competitive rates',
      flexiblePlans: 'Flexible plans',
      tradeInService: 'Trade-in service',
      tradeInDesc: 'Instant valuation and fair market pricing for your current vehicle.',
      instantValuationLabel: 'Instant valuation',
      fairPricing: 'Fair pricing',
      simpleProcess: 'Simple process',
    },
    common: {
      inventory: 'Inventory',
      finance: 'Finance',
      sellTrade: 'Sell/Trade-in',
      contact: 'Contact',
      signIn: 'Sign In',
      saved: 'Saved',
      searchPlaceholder: 'What kind of car are you looking for?',
      prev: 'Previous',
      next: 'Next',
      goToSlide: 'Go to slide',
      skipToContent: 'Skip to main content',
      backToTop: 'Back to top',
    },
    profile: {
      signInToView: 'Sign in to view your profile',
      personalInfo: 'Personal Info',
      paymentMethods: 'Payment Methods',
      documents: 'Documents',
      security: 'Security',
      notifications: 'Notifications',
      preferences: 'Preferences',
      personalInformation: 'Personal Information',
      paymentMethodsTitle: 'Payment Methods',
      documentsTitle: 'Documents',
      securitySettings: 'Security Settings',
      preferencesTitle: 'Preferences',
      customizeExperience: 'Customize your experience',
      loadingPreferences: 'Loading preferences...',
      appearance: 'Appearance',
      theme: 'Theme',
      light: 'Light',
      whiteBackground: 'White background',
      dark: 'Dark',
      darkBackground: 'Dark background',
      languageAndRegion: 'Language & Region',
      language: 'Language',
      timezone: 'Timezone',
      dateFormat: 'Date Format',
      currency: 'Currency',
      uploadDocuments: 'Upload Documents',
      yourDocuments: 'Your Documents',
      chooseFiles: 'Choose Files',
      uploading: 'Uploading...',
      person: 'Person',
      addCard: 'Add Card',
      adding: 'Adding...',
      save: 'Save',
      cancel: 'Cancel',
      remove: 'Remove',
    },
    footer: {
      companyDesc: 'We are committed to providing exceptional automotive solutions through our comprehensive inventory of premium pre-owned vehicles, transparent pricing structures, and unparalleled customer service excellence.',
      shop: 'Shop',
      browseInventory: 'Browse Inventory',
      financingOptions: 'Financing Options',
      sellYourCar: 'Sell Your Car',
      tradeInValue: 'Trade-In Value',
      company: 'Company',
      aboutUs: 'About Us',
      contact: 'Contact',
      careers: 'Careers',
      blog: 'Blog',
      support: 'Support',
      faq: 'FAQ',
      shippingInfo: 'Shipping Info',
      warranty: 'Warranty',
      returns: 'Returns',
      customerFeedbackNps: 'Customer feedback',
      purchaseExperienceSurvey: 'Suggest features & improvements',
      legal: 'Legal',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
      cookiesPolicy: 'Cookies Policy',
      registerPartnerDealer: 'Register as Partner Dealer',
      allRightsReserved: 'All rights reserved.',
    },
    infoPages: {
      contactCta: 'Contact us',
      about: {
        title: 'About MyCarSlot',
        subtitle: 'Quality pre-owned vehicles with transparent pricing and customer-first service.',
        body: 'MyCarSlot is a licensed dealership focused on honest cars and straight deals. Browse our inventory online, schedule a visit, or reach out with any questions—we are here to help at every step.',
      },
      blog: {
        title: 'Blog',
        subtitle: 'Tips, news, and stories from our team.',
        body: 'We are preparing articles on buying smart, financing basics, and caring for your vehicle. Check back soon, or contact us if you would like to hear about a specific topic.',
      },
      faq: {
        title: 'Frequently asked questions',
        subtitle: 'Quick answers to common questions.',
        body: 'Questions about financing, delivery, warranties, or scheduling a test drive? Our team can walk you through options tailored to your situation. Contact us and we will respond as soon as possible.',
      },
      shipping: {
        title: 'Shipping & delivery',
        subtitle: 'Delivery options for your area.',
        body: 'Delivery availability and pricing depend on your location and the vehicle you choose. Contact us with your ZIP code for a personalized delivery quote—no obligation.',
      },
      warranty: {
        title: 'Warranty information',
        subtitle: 'Coverage options for your peace of mind.',
        body: 'Warranty terms vary by vehicle and may include remaining factory coverage or optional extended plans. Ask us about coverage on any listing before you buy.',
      },
      returns: {
        title: 'Returns policy',
        subtitle: 'How we handle concerns after purchase.',
        body: 'We want you confident in your purchase. If something is not right, contact us promptly so we can review your situation and work toward a fair resolution under applicable law and our policies.',
      },
    },
    auth: {
      welcome: 'Welcome',
      enterEmailToStart: 'Enter your email to get started',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      continue: 'Continue',
      checking: 'Checking...',
      createAccount: 'Create Account',
      completeProfile: 'Complete your profile',
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      password: 'Password',
      createPassword: 'Create password',
      minChars: '8+ chars',
      number: 'Number',
      letter: 'Letter',
      noSpaces: 'No spaces',
      createAccountButton: 'Create Account',
      creating: 'Creating...',
      welcomeBack: 'Welcome Back',
      signInToAccount: 'Sign in to your account',
      noAccount: "Don't have an account?",
      createAnAccount: 'Create an account',
      enterPassword: 'Enter password',
      keepSignedIn: 'Keep me signed in',
      forgotPassword: 'Forgot your password?',
      twoFactorCode: 'Two-Factor Authentication Code',
      enter6DigitCode: 'Enter 6-digit code',
      enterCodeFromApp: 'Enter the 6-digit code from your authenticator app',
      verify: 'Verify',
      signingIn: 'Signing in...',
      verifying: 'Verifying...',
      resetPassword: 'Reset Password',
      sendResetCode: "We'll send you a reset code",
      sendResetCodeButton: 'Send Reset Code',
      sending: 'Sending...',
      newPassword: 'New Password',
      enterCodeAndPassword: 'Enter code and new password',
      resetCode: 'Reset Code',
      resetCodePlaceholder: '6-digit code',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Confirm password',
      resetPasswordButton: 'Reset Password',
      resetting: 'Resetting...',
    },
    carCard: {
      used: 'USED',
      certified: 'CERTIFIED',
      estimatedPrice: 'Estimated Price',
      priceTbd: 'Price TBD',
      estimatedMo: 'Estimated',
      cashDown: '$0 cash down',
      freeDelivery: 'Free Delivery',
      shipping: 'shipping',
      deliveredBefore: 'Delivered before',
      calculatingShipping: 'Calculating shipping...',
      compare: 'Compare',
      addToWishlist: 'Add to wishlist',
      expectedLaunch: 'EXPECTED LAUNCH :',
      priceDrop: 'Price Drop',
      was: 'was',
      vehicle: 'Vehicle',
      prequalCta: 'Get pre-qualified to see your personalized monthly payment',
      deliveryQuote: 'Contact for delivery quote',
    },
    carDetails: {
      priceHistoryTitle: 'Price history',
      priceWentDown: 'Price reduced by',
      priceWentUp: 'Price increased by',
    },
    financing: {
      calcTitle: 'Payment calculator',
      calcDisclaimer: 'Estimates only. Actual rate and payment depend on credit approval and lender terms.',
      downPayment: 'Down payment',
      apr: 'APR (%)',
      term: 'Loan term',
      months: 'months',
      estimatedMonthly: 'Estimated monthly',
      amountFinanced: 'Amount financed',
      totalInterest: 'Total interest',
      totalPaid: 'Total paid over term',
    },
    market: {
      goodDeal: 'Good deal',
      goodDealTitle: 'Priced below similar vehicles in our inventory',
      aboveMarket: 'Above market',
      fairPrice: 'Fair vs market',
      vs: 'vs',
      similar: 'similar',
      belowPeers: 'below similar',
    },
    cookie: {
      weUseCookies: 'We use cookies',
      cookieMessage: 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or reject non-essential cookies.',
      customize: 'Customize',
      rejectAll: 'Reject All',
      acceptAll: 'Accept All',
      cookiePreferences: 'Cookie Preferences',
      managePreferences: 'Manage your cookie preferences. You can enable or disable different types of cookies below.',
      essentialCookies: 'Essential Cookies',
      essentialDesc: 'Required for the website to function properly. These cannot be disabled.',
      analyticsCookies: 'Analytics Cookies',
      analyticsDesc: 'Help us understand how visitors interact with our website.',
      marketingCookies: 'Marketing Cookies',
      marketingDesc: 'Used to deliver personalized advertisements and track campaign performance.',
      dataSharing: 'Data Sharing',
      dataSharingDesc: 'Allow us to share your data with trusted third-party partners.',
      savePreferences: 'Save Preferences',
      saving: 'Saving...',
    },
    notFound: {
      carNotFound: 'Car not found',
      carNotFoundMessage: "The car you are looking for doesn't exist or has been removed.",
      backToInventory: 'Back to Inventory',
      pageNotFound: 'Page not found',
      pageNotFoundMessage: "The page you are looking for doesn't exist.",
      goHome: 'Go Home',
      goBackToCar: 'Go Back to Car Details Page',
    },
    contact: {
      pageEyebrow: 'Support',
      getInTouch: 'Contact us',
      getInTouchSub: 'Questions about inventory, financing, or delivery? Pick the fastest option below — or send us a message and we\'ll reply during store hours.',
      quickActionsTitle: 'Reach us your way',
      chatAction: 'Live chat',
      chatActionDesc: 'Fast answers while you browse',
      phoneAction: 'Call us',
      faceTimeAction: 'FaceTime',
      faceTimeActionDesc: 'Apple devices · store hours',
      emailAction: 'Email',
      emailActionDesc: 'sales@mycarslot.com',
      faqAction: 'FAQ & help',
      faqActionDesc: 'Browse common questions',
      formLead: 'We typically respond the same business day during store hours.',
      directions: 'Get directions',
      contactInfo: 'Contact Information',
      contactInfoText: 'Walk-ins welcome. Schedule a test drive online and we\'ll have your vehicle ready.',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      hours: 'Hours',
      sendMessage: 'Send us a message',
      name: 'Name',
      subject: 'Subject',
      message: 'Message',
      send: 'Send',
      sending: 'Sending...',
      successMessage: 'Thank you! Your message has been sent successfully.',
      businessHours: 'Business Hours',
      fullName: 'Full Name',
      selectSubject: 'Select a subject',
      generalInquiry: 'General Inquiry',
      salesQuestion: 'Sales Question',
      serviceSupport: 'Service & Support',
      tradeIn: 'Trade-In',
      other: 'Other',
      messagePlaceholder: 'Tell us how we can help you...',
      sendButton: 'Send Message',
      visitDealership: 'Visit Our Dealership',
    },
    faqSupport: {
      eyebrow: 'Help center',
      heroTitle: 'FAQ & support',
      heroSubtitle: 'Find quick answers about buying, financing, selling, delivery, and your account — or reach our team directly.',
      hubSubtitle: 'Choose a topic below to browse common questions, or contact our team for personal help.',
      helpCenter: 'Help center',
      searchPlaceholder: 'Search questions...',
      topicsLabel: 'FAQ topics',
      browseByTopic: 'Browse by topic',
      popularLabel: 'Popular questions',
      quickLinksLabel: 'Quick links',
      hoursTitle: 'Support hours',
      hoursWeekday: 'Mon–Fri: 9:00 AM – 7:00 PM',
      hoursSaturday: 'Sat: 9:00 AM – 6:00 PM · Sun: 10:00 AM – 5:00 PM',
      allTopics: 'All topics',
      resultCount: '{{count}} questions',
      noResults: 'No matching questions found',
      noResultsHint: 'Try another search term or contact our team for personalized help.',
      backToTopic: 'Back to {{topic}}',
      moreInTopic: 'More in {{topic}}',
      expertTitle: 'Reach a car-buying expert',
      expertMessage: 'Use our contact form — we help with inventory, Auction to Own, financing applications, sell/trade, and delivery.',
      expertPhone: `Call ${PHONE} · Mon–Fri 9–7, Sat 9–6, Sun 10–5 · Ocean Springs, MS`,
      contactUsTitle: 'Contact us',
      contactChatBody:
        'Questions about inventory, financing, delivery, or your account? Click below to open live chat — our team responds during store hours.',
      contactChatBtn: 'Chat with us',
      contactHqTitle: 'Mycarslot HQ',
      contactHqBody:
        '6314 Washington Ave, Ocean Springs, MS 39564\nMon–Fri 9 AM–7 PM · Sat 9 AM–6 PM · Sun 10 AM–5 PM\nsales@mycarslot.com',
      contactChatLabel: 'Chat with us',
      contactPhoneLabel: 'Call us',
      contactPhoneValue: PHONE,
      contactCallBtn: 'Call {{phone}}',
      contactCallAria: 'Call {{phone}}',
      contactPhoneHint: 'Mon–Fri 9–7 · Sat 9–6 · Sun 10–5',
      contactEmailLabel: 'Email us',
      contactEmailValue: 'sales@mycarslot.com',
      contactEmailHint: 'Replies during store hours',
      contactVisitLabel: 'Visit us',
      contactVisitValue: 'Ocean Springs, MS',
      contactVisitHint: '6314 Washington Ave',
      categoryDesc: {
        buying: 'Inventory, Auction to Own ($150 sourcing fee), pricing, and holds',
        finding: 'How we work, budgeting, compare up to 6 cars, and saved searches',
        financing: 'Get Financed application, Lendmark partner, calculator, and lender review',
        payments: 'Due dates, payment methods, early payoff, and missed payments',
        selling: 'Sell/Trade-in estimates, in-person verification, and trade-in value',
        delivery: 'ZIP estimates from ~$299, dealership pickup, and shipping quotes',
        storeVisit: 'Test drives, appointments, walk-ins, and store hours',
        warranty: 'Factory warranty, extended plans, and coverage details',
        sourcing: 'Profile → Interested In, $150 non-refundable fee, and timelines',
        account: 'Wishlist, saved searches, appointments, and profile settings',
        security: 'Account security, privacy, and suspicious contact',
        recalls: 'Open recalls, repairs, and checking vehicle status',
        afterPurchase: 'Returns, documents, and post-sale support',
        contactSupport: 'Phone, email, hours, contact form, and live chat',
        policiesLegal: 'Privacy, terms, cookies, shipping, returns, and warranty policies',
        aboutMyCarSlot: 'Who we are, what we offer, and why shop with us',
      },
      categories: {
        buying: 'Buying a car',
        finding: 'Finding a car',
        financing: 'Financing',
        payments: 'Making payments',
        selling: 'Sell & trade-in',
        delivery: 'Delivery & pickup',
        storeVisit: 'Store visits & test drives',
        warranty: 'Warranties & protection',
        sourcing: 'Auction to Own & sourcing',
        account: 'Your account',
        security: 'Security & privacy',
        recalls: 'Safety recalls',
        afterPurchase: 'After you buy',
        contactSupport: 'Contact & support',
        policiesLegal: 'Policies & legal',
        aboutMyCarSlot: 'About MyCarSlot',
      },
      questions: {
        buying: {
          browseInventory: {
            q: 'How do I browse and compare vehicles online?',
            a: 'Open Inventory to filter by make, model, price, body type, mileage, and more. Save favorites to your wishlist, compare listings side by side, and schedule a visit when you are ready.',
          },
          auctionToOwn: {
            q: 'What is Auction to Own and how do I start?',
            a: 'Tell us the make, model, and budget you want and we will source the vehicle through our auction network for a flat fee under $150. You keep every dollar saved compared with traditional retail markup.',
          },
          vehicleHistory: {
            q: 'Do your vehicles include history and inspection details?',
            a: 'We provide transparent listing information and can walk you through history reports, condition notes, and inspection details for any vehicle you are considering.',
          },
          certifiedQuality: {
            q: 'How do you evaluate vehicles before listing them?',
            a: 'We review key mechanical and cosmetic details before listing a vehicle and share condition notes on the listing whenever available so you can shop with confidence.',
          },
          pricingTransparent: {
            q: 'Is the price on the listing the final price?',
            a: 'Listed prices are transparent starting points. Tax, title, registration, optional products, and delivery fees may apply depending on your location and deal structure. We will walk you through the full out-the-door estimate before you sign.',
          },
          holdVehicle: {
            q: 'Can I hold or reserve a vehicle while I decide?',
            a: 'Availability can change quickly. Ask our team about hold or deposit options on the specific vehicle you want — terms vary by listing and store policy.',
          },
          outOfState: {
            q: 'Can I buy a vehicle if I live in another state?',
            a: 'Yes. Many customers purchase from out of state. We help coordinate paperwork, taxes, and delivery or pickup based on your home state requirements.',
          },
          depositRequired: {
            q: 'Is a deposit required to purchase?',
            a: 'Deposits may be requested to hold a vehicle or begin paperwork on certain deals. Amount and refund terms are explained in writing before you pay.',
          },
          scheduleTestDrive: {
            q: 'How do I schedule a test drive from a vehicle page?',
            a: '',
          },
          interestedInButton: {
            q: 'What does "Interested in this car" do on a listing?',
            a: '',
          },
          carfaxOnListing: {
            q: 'Can I see CARFAX or history on a vehicle page?',
            a: '',
          },
          inspection150Points: {
            q: 'What is the 150+ point inspection on your site?',
            a: '',
          },
        },
        finding: {
          howItWorks: {
            q: 'How does MyCarSlot work?',
            a: 'Browse our inventory online, get pre-qualified for financing, schedule a visit or delivery, and complete your purchase with transparent pricing and support at every step.',
          },
          rightBudget: {
            q: 'How do I find a car that fits my budget?',
            a: 'Use price filters on Inventory and the payment calculator on our Finance page to estimate monthly payments based on price, down payment, term, and APR.',
          },
          compareTool: {
            q: 'Can I compare vehicles before I decide?',
            a: 'Yes. Select multiple vehicles on Inventory to compare key specs, pricing, and features side by side before you schedule a visit or apply for financing.',
          },
          savedSearchAlerts: {
            q: 'How do saved searches and email alerts work?',
            a: 'Sign in and use Save Search on Inventory to store your filters. We can notify you when new vehicles match your criteria.',
          },
          shopByType: {
            q: 'Can I shop by body type or lifestyle needs?',
            a: 'Yes. Filter Inventory by SUV, sedan, truck, hybrid, and other categories — or use search to narrow by features that matter to you.',
          },
          newVsUsed: {
            q: 'Do you sell new and used vehicles?',
            a: 'Our focus is quality pre-owned inventory. Listings show year, mileage, and condition details so you can compare value across options.',
          },
          helpChoosing: {
            q: 'Can someone help me choose the right vehicle?',
            a: 'Absolutely. Contact us with your budget, family size, and must-have features — our team can suggest matches from current inventory or sourcing options.',
          },
          buyingStepsOverview: {
            q: 'What are the steps to buy a car on MyCarSlot?',
            a: '',
          },
          liveChatHelp: {
            q: 'Can I chat with someone while browsing the site?',
            a: '',
          },
          similarVehicles: {
            q: 'How does MyCarSlot recommend similar vehicles?',
            a: '',
          },
        },
        financing: {
          preQual: {
            q: 'How does the Get Financed application work?',
            a: 'Start with Check my rate on our Finance page or Get Financed flow. Pre-qualification is quick and gives you estimated terms so you can shop with confidence before a full credit application.',
          },
          lendmark: {
            q: 'Who provides auto financing?',
            a: 'We partner with Lendmark Financial Services for competitive rates and flexible terms nationwide. Final approval, rates, and terms depend on your credit profile, vehicle, and lender guidelines.',
          },
          calculator: {
            q: 'Can I estimate my monthly payment before I apply?',
            a: 'Yes. Use the payment calculator on our Finance page to adjust price, down payment, term, and APR and see estimated payments in real time.',
          },
          softCredit: {
            q: 'Will applying on Get Financed affect my credit score?',
            a: 'Pre-qualification uses a soft inquiry that does not impact your credit score. A hard inquiry may occur only if you proceed with a full credit application and lender approval process.',
          },
          downPayment: {
            q: 'How much down payment do I need?',
            a: 'Down payment requirements vary by credit profile, vehicle price, and lender guidelines. Pre-qualification and our calculator help you explore scenarios before you apply.',
          },
          coSigner: {
            q: 'Can I use a co-signer on my loan?',
            a: 'Co-signers may be allowed depending on lender policy and combined credit strength. Mention this during your finance application so we can review eligible options.',
          },
          refinance: {
            q: 'Can I refinance my auto loan later?',
            a: 'Refinancing is handled directly with lenders and depends on your credit, vehicle value, and current rates. Lendmark or your existing lender can advise on refinance eligibility.',
          },
          rateLock: {
            q: 'Will my approved rate change before I buy?',
            a: 'Rate offers can expire and may change if your credit profile, vehicle, or market conditions change before funding. Your finance specialist will confirm timing and any rate-lock details.',
          },
          getFinancedRequirements: {
            q: 'What information do I need for the Get Financed application?',
            a: '',
          },
          calculatorOnCarPage: {
            q: 'Can I calculate payments on a specific vehicle page?',
            a: '',
          },
        },
        payments: {
          firstPayment: {
            q: 'When is my first payment due?',
            a: 'Your first payment date depends on your lender and contract. Lendmark and our finance team will confirm your due date and amount before you finalize your purchase.',
          },
          paymentMethods: {
            q: 'What payment methods can I use for my auto loan?',
            a: 'Payment options depend on your lender and may include online payments, automatic drafts, phone payments, or mail. Ask your finance specialist for the options available on your account.',
          },
          payOffEarly: {
            q: 'Can I pay off my loan early?',
            a: 'In most cases, yes. Early payoff terms vary by lender and contract. Contact your lender or our team for a payoff quote and any applicable details.',
          },
          missedPayment: {
            q: 'What should I do if I miss a payment?',
            a: 'Contact your lender or our team as soon as possible. Many lenders offer hardship options or payment plans when you reach out before an account becomes severely delinquent.',
          },
          autopaySetup: {
            q: 'How do I set up automatic payments?',
            a: 'Most lenders offer autopay through their online portal or by phone. Ask your finance specialist for enrollment steps and whether autopay qualifies for any rate or fee benefits.',
          },
          changeDueDate: {
            q: 'Can I change my payment due date?',
            a: 'Due date changes depend on lender policy and may be limited to once per loan term. Contact your lender directly to request a due date adjustment.',
          },
          payoffQuote: {
            q: 'How do I get a payoff quote?',
            a: 'Request a payoff quote from your lender for a specific date. The quote includes remaining principal, accrued interest, and any fees required to close the loan.',
          },
        },
        selling: {
          instantOffer: {
            q: 'How do online sell and trade-in estimates work?',
            a: 'Visit Sell/Trade-in and enter your license plate, VIN, or vehicle details. We will provide a competitive offer you can accept, adjust, or apply toward your next purchase.',
          },
          tradeInProcess: {
            q: 'How does the trade-in process work?',
            a: 'After you receive an offer, bring your vehicle in for verification. We confirm condition and paperwork, then apply the value toward your purchase or arrange payment if you are selling outright.',
          },
          payDifference: {
            q: 'Can I trade in a car and finance the difference?',
            a: 'Yes. Trade-in value can reduce the amount you finance on your next vehicle. Our team and financing partners help structure a payment that fits your budget.',
          },
          offerExpire: {
            q: 'How long is my sell or trade-in offer valid?',
            a: 'Offer expiration varies based on market conditions and verification. Your offer details will show a validity period — bring the vehicle in before it expires for confirmation.',
          },
          negativeEquity: {
            q: 'What if I owe more than my trade-in is worth?',
            a: 'Negative equity can sometimes be rolled into a new loan depending on credit and lender approval. Our team can review your payoff amount and explain options clearly.',
          },
          paperworkNeeded: {
            q: 'What paperwork do I need to sell or trade my car?',
            a: 'Typically bring your title or lien release, valid ID, registration, and all keys. If you still owe on the loan, we coordinate payoff with your lender during the process.',
          },
          sellWithoutBuying: {
            q: 'Can I sell my car without buying one from you?',
            a: 'Yes. You can request an outright purchase offer through Sell/Trade-in without purchasing another vehicle from our inventory.',
          },
          sellVsTradePages: {
            q: 'What is the difference between /sell and Sell/Trade-in?',
            a: '',
          },
        },
        delivery: {
          deliveryOptions: {
            q: 'Do you offer home delivery?',
            a: 'Delivery may be available depending on your location and the vehicle selected. Contact us with your ZIP code for options, timing, and a personalized quote.',
          },
          zipDeliveryEstimate: {
            q: 'How does the ZIP code delivery estimate on a vehicle page work?',
            a: '',
          },
          deliveryCost: {
            q: 'How much does delivery cost?',
            a: 'Delivery pricing varies by distance, vehicle, and service level. Some listings may include promotional delivery offers — ask our team for a quote with no obligation.',
          },
          pickup: {
            q: 'Can I pick up my vehicle at the dealership?',
            a: 'Absolutely. Many customers choose in-store pickup at our Ocean Springs location. We will prepare your paperwork and walk you through delivery at pickup.',
          },
          deliveryStates: {
            q: 'Which states do you deliver to?',
            a: 'Delivery coverage depends on distance, regulations, and vehicle availability. Share your ZIP code with our team for a quick eligibility check and quote.',
          },
          trackDelivery: {
            q: 'Can I track my delivery status?',
            a: 'Once scheduled, our team provides estimated windows and updates by phone or email. Ask your specialist for the latest status on your delivery day.',
          },
          deliveryInspection: {
            q: 'Should I inspect the vehicle at delivery?',
            a: 'Yes. Review the exterior, interior, mileage, and features at handoff. Note any concerns immediately so we can address them before you take possession.',
          },
          tempTags: {
            q: 'Will I get temporary tags or plates at delivery?',
            a: 'Temporary tag rules vary by state. We explain what you will receive at pickup or delivery and what follow-up steps are needed for permanent registration.',
          },
        },
        storeVisit: {
          scheduleVisit: {
            q: 'How do I schedule a test drive or store visit?',
            a: 'Choose a vehicle on our site and book an appointment online, or contact us to arrange a time. You can also manage visits from Appointments in your account.',
          },
          signInToBook: {
            q: 'Do I need an account to book a test drive online?',
            a: '',
          },
          whatToBring: {
            q: 'What should I bring to my appointment?',
            a: 'Bring a valid driver\'s license, proof of insurance if you plan to test drive, and any trade-in details. If you are financing, bring income or residence documents if requested by your lender.',
          },
          walkIns: {
            q: 'Can I visit the store without an appointment?',
            a: 'Walk-ins are welcome during business hours when availability allows. Scheduling ahead helps us have the vehicle ready and reduces your wait time.',
          },
          howLongVisit: {
            q: 'How long does a typical visit or test drive take?',
            a: 'Plan 30–60 minutes for a test drive and initial questions. Financing or trade-in reviews may take longer — we will help you schedule enough time.',
          },
          bringPassenger: {
            q: 'Can I bring someone with me to the appointment?',
            a: 'Yes. Many buyers bring a spouse, family member, or friend. Additional drivers who will test drive must meet license and insurance requirements.',
          },
          storeHours: {
            q: 'What are your store hours?',
            a: 'We are open Monday–Friday 9 AM–7 PM, Saturday 9 AM–6 PM, and Sunday 10 AM–5 PM at our Ocean Springs location. Hours may vary on holidays.',
          },
        },
        warranty: {
          factoryWarranty: {
            q: 'Does my vehicle still have factory warranty?',
            a: 'Many pre-owned vehicles include remaining factory warranty based on age and mileage. Ask us about specific coverage on any listing before you buy.',
          },
          warrantyPolicyPage: {
            q: 'Where can I read your warranty policy online?',
            a: '',
          },
          extendedPlans: {
            q: 'Do you offer extended protection plans?',
            a: 'Optional extended service or protection plans may be available depending on the vehicle and your needs. Our team can explain options and pricing during your purchase.',
          },
          whatIsCovered: {
            q: 'What is typically covered under warranty?',
            a: 'Coverage varies by plan and may include major powertrain components, roadside assistance, or other benefits. We will walk you through what applies to your vehicle before you sign.',
          },
          transferWarranty: {
            q: 'Is warranty coverage transferable to the next owner?',
            a: 'Factory and extended warranties may be transferable depending on plan terms and remaining coverage. Ask us for details on the specific vehicle you are buying.',
          },
          claimProcess: {
            q: 'How do I file a warranty claim?',
            a: 'Claims are usually filed through the warranty administrator or an authorized repair facility. We can provide contact details and explain the process for your plan.',
          },
          exclusions: {
            q: 'What is usually not covered by warranty?',
            a: 'Wear items, maintenance services, accidental damage, and unauthorized modifications are commonly excluded. Review your plan documents for the full exclusion list.',
          },
        },
        sourcing: {
          programOverview: {
            q: 'What is the Auction to Own & vehicle sourcing program?',
            a: 'Tell us what you want — make, model, budget, and preferences — and we source the vehicle through our auction and partner network so you avoid traditional retail markup.',
          },
          sourcingFee: {
            q: 'How much is the vehicle sourcing fee?',
            a: 'Our sourcing fee is a flat charge under $150 for qualifying requests. You keep the savings compared with typical retail pricing on the same vehicle.',
          },
          payWithSquare: {
            q: 'How do I pay the vehicle sourcing fee?',
            a: '',
          },
          readFeeAgreement: {
            q: 'Where can I read the Vehicle Sourcing Fee Agreement?',
            a: '',
          },
          howLong: {
            q: 'How long does vehicle sourcing take?',
            a: 'Timelines vary by vehicle availability, auction schedules, and transport. We will give you an estimated timeline after you submit your request and keep you updated throughout the process.',
          },
          cancelRequest: {
            q: 'Can I cancel a sourcing request?',
            a: 'Cancellation terms depend on how far sourcing has progressed and any costs already incurred. Contact us as early as possible if your plans change.',
          },
          specifyOptions: {
            q: 'Can I request specific colors, trims, or options?',
            a: 'Yes. Share your preferences when you submit a sourcing request. We will search for the closest match within your budget and timeline expectations.',
          },
          outOfBudget: {
            q: 'What if the vehicle I want is above my budget?',
            a: 'We can suggest comparable models, model years, or sourcing strategies to hit your target. Adjusting term or down payment may also help on financing.',
          },
        },
        account: {
          createAccount: {
            q: 'Do I need an account to shop on MyCarSlot?',
            a: 'You can browse inventory without an account. Creating a free account lets you save wishlists, saved searches, appointments, and financing progress in one place.',
          },
          profileTabs: {
            q: 'What can I manage in my Profile?',
            a: '',
          },
          appointments: {
            q: 'Where do I view or manage my appointments?',
            a: 'Sign in and open Appointments in your account menu to review upcoming visits, reschedule, or cancel when plans change.',
          },
          wishlist: {
            q: 'How do saved cars and searches work?',
            a: 'Use the heart icon to save vehicles to your wishlist and Save Search on Inventory to get notified when new matches arrive. Both require a free account.',
          },
          manageSavedSearches: {
            q: 'How do I manage saved searches and email alerts?',
            a: '',
          },
          uploadDocuments: {
            q: 'Can I upload documents for my purchase or financing?',
            a: '',
          },
          resetPassword: {
            q: 'How do I reset my password?',
            a: 'Use Forgot password on the sign-in page and follow the email instructions. If you no longer have access to your email, contact us for account recovery help.',
          },
          updateProfile: {
            q: 'How do I update my contact information?',
            a: 'Sign in and open Settings or Profile to update your name, phone, and address. Keep details current so we can reach you about appointments and financing.',
          },
          deleteAccount: {
            q: 'Can I delete my MyCarSlot account?',
            a: 'Contact us to request account deletion. Some records may be retained as required for legal, finance, or transaction purposes.',
          },
          emailNotifications: {
            q: 'How do I manage email or alert preferences?',
            a: 'Saved search and appointment notifications depend on your account settings and the features you use. Ask our team if you need help adjusting communication preferences.',
          },
        },
        security: {
          passwordAccount: {
            q: 'How do I keep my MyCarSlot account secure?',
            a: 'Use a strong unique password, sign out on shared devices, and enable two-factor authentication if available on your account. Never share verification codes with anyone.',
          },
          secureFinanceApplication: {
            q: 'Is my information secure on the Get Financed application?',
            a: '',
          },
          dataPrivacy: {
            q: 'How is my personal information protected?',
            a: 'We use industry-standard safeguards for account and application data. Read our Privacy Policy for details on what we collect, how we use it, and your choices.',
          },
          suspiciousContact: {
            q: 'What if I receive suspicious messages about my purchase?',
            a: 'MyCarSlot will not ask for passwords or payment by gift card over text or email. If something seems off, contact us directly using the phone number or contact form on our website.',
          },
          twoFactor: {
            q: 'How do I turn on two-factor authentication?',
            a: 'If two-factor authentication is available on your account, enable it in Profile or Settings for an extra layer of security when signing in.',
          },
          sharedComputer: {
            q: 'What should I do after using a shared or public computer?',
            a: 'Always sign out of your account, close the browser, and avoid saving passwords on devices you do not control.',
          },
          reportFraud: {
            q: 'How do I report suspected fraud?',
            a: 'Contact us immediately by phone or through the contact form with details of the suspicious activity. Do not send money or personal data to unverified contacts.',
          },
        },
        recalls: {
          openRecalls: {
            q: 'Do you sell vehicles with open safety recalls?',
            a: 'We check recall status when possible and disclose known open recalls on applicable listings. Ask us about recall status on any vehicle you are considering.',
          },
          recallRepair: {
            q: 'What happens if a recall is issued after I buy?',
            a: 'Safety recalls are typically repaired at no cost through authorized brand dealers. We can help point you to the right next steps if a recall affects your vehicle after purchase.',
          },
          checkRecall: {
            q: 'How can I check recall status on a vehicle?',
            a: 'You can ask our team for recall information on a specific VIN or check official government recall databases. We are happy to help before you buy.',
          },
          recallBeforeDelivery: {
            q: 'Are open recalls repaired before delivery?',
            a: 'When possible and appropriate, known open recalls may be addressed before delivery or pickup. We will explain the plan for any recall on your specific vehicle.',
          },
          manufacturerRecall: {
            q: 'Who performs manufacturer recall repairs?',
            a: 'Brand authorized dealers typically perform recall repairs at no charge to the owner. We can help you locate the right dealer and schedule service.',
          },
          fleetRecalls: {
            q: 'Do fleet or rental history vehicles have different recall rules?',
            a: 'Recall obligations apply to the vehicle regardless of prior use. Always verify open campaigns on the VIN and confirm repair status before purchase.',
          },
        },
        afterPurchase: {
          returns: {
            q: 'What if something is not right after I purchase?',
            a: 'Contact us promptly if you have a concern. We will review your situation and work toward a fair resolution under applicable law and our policies.',
          },
          returnsPolicyPage: {
            q: 'Where can I read your returns policy?',
            a: '',
          },
          serviceHelp: {
            q: 'Who do I contact for post-purchase questions?',
            a: 'Reach our team by phone, email, or the contact form. For financing questions, include your application details so we can connect you with the right specialist quickly.',
          },
          documents: {
            q: 'When will I receive my title and registration documents?',
            a: 'Timing depends on your state, lender, and whether you traded in a vehicle. Our team will explain what to expect and keep you updated on any paperwork still outstanding.',
          },
          insuranceProof: {
            q: 'When do I need proof of insurance?',
            a: 'Proof of insurance is typically required before you drive the vehicle off the lot or take delivery. Your lender may also require specific coverage levels.',
          },
          tempPlate: {
            q: 'How do temporary plates or tags work after purchase?',
            a: 'We issue or explain temporary documentation based on state rules so you can drive legally while permanent plates are processed.',
          },
          lenderLien: {
            q: 'How does my lender show on the title?',
            a: 'If you finance, the lender usually holds a lien on the title until the loan is paid off. We coordinate lien notation with your state and lender.',
          },
          satisfactionSurvey: {
            q: 'Will I receive a customer satisfaction survey?',
            a: 'You may receive a follow-up survey so we can improve our service. Your feedback helps us serve future customers better.',
          },
          satisfactionSurveyLink: {
            q: 'Where do I complete a satisfaction survey?',
            a: '',
          },
        },
        contactSupport: {
          howToReachUs: {
            q: 'How do I contact MyCarSlot?',
            a: '',
          },
          phoneAndEmail: {
            q: 'What is your phone number and email?',
            a: '',
          },
          businessHours: {
            q: 'What are your support and store hours?',
            a: '',
          },
          contactForm: {
            q: 'How do I use the contact form on your website?',
            a: '',
          },
          bestChannel: {
            q: 'Should I call, email, or use the contact form?',
            a: '',
          },
          visitLocation: {
            q: 'Where is your dealership located?',
            a: '',
          },
          chatSupport: {
            q: 'Can I get help through live chat on the website?',
            a: '',
          },
        },
        policiesLegal: {
          privacyPolicy: {
            q: 'Where can I read your Privacy Policy?',
            a: '',
          },
          termsOfService: {
            q: 'Where can I read your Terms of Service?',
            a: '',
          },
          cookiesPolicy: {
            q: 'How does MyCarSlot use cookies?',
            a: '',
          },
          shippingPolicyPage: {
            q: 'Where is your shipping and delivery policy?',
            a: '',
          },
          returnsPolicyPage: {
            q: 'Where is your returns policy?',
            a: '',
          },
          warrantyPolicyPage: {
            q: 'Where is your warranty policy online?',
            a: '',
          },
          findAllPolicies: {
            q: 'Where do I find all legal and policy pages?',
            a: '',
          },
        },
        aboutMyCarSlot: {
          whoWeAre: {
            q: 'What is MyCarSlot?',
            a: '',
          },
          whatWeSell: {
            q: 'What kind of vehicles does MyCarSlot sell?',
            a: '',
          },
          ourLocation: {
            q: 'Where is MyCarSlot based?',
            a: '',
          },
          whyChooseUs: {
            q: 'Why should I shop with MyCarSlot?',
            a: '',
          },
          aboutPage: {
            q: 'Where can I learn more about MyCarSlot online?',
            a: '',
          },
          howWeHelp: {
            q: 'How does MyCarSlot help from search to purchase?',
            a: '',
          },
        },
      },
      support: {
        title: 'Still need help?',
        subtitle: 'Our specialists can walk you through the next step — by phone, email, or message.',
        callTitle: 'Call us',
        callDesc: `${PHONE} · Mon–Sat`,
        emailTitle: 'Email us',
        emailDesc: 'sales@mycarslot.com',
        contactTitle: 'Send a message',
        contactDesc: 'We typically reply within one business day.',
        contactCta: 'Contact us',
        inventoryCta: 'Browse inventory',
        financeCta: 'Explore financing',
        sellCta: 'Sell or trade-in',
      },
    },
    inventory: {
      compareCars: 'Compare Cars',
      saveSearch: 'Save Search',
      filters: 'Filters',
      sortBy: 'Sort by',
      noCars: 'No vehicles match your filters.',
      clearFilters: 'Clear filters',
      clearAll: 'Clear All',
      results: 'results',
      filter: 'Filter',
      activeFiltersLabel: 'Active Filters:',
      saveSearchTitle: 'Save current search',
      share: 'Share',
      linkCopied: 'Link copied!',
      shareFilteredView: 'Share filtered view',
      copied: 'Copied!',
      comparingItems: 'Comparing {{current}} of {{max}} items',
      removeFromComparison: 'Remove from comparison',
      noCarsMatch: 'No cars match your filters',
      tryClearingFilters: 'Try clearing your filters or adjusting your search criteria',
      totalCarsInDb: 'Total cars in database:',
      availableCars: 'Available cars:',
      pleaseLogInToSave: 'Please log in to save your search',
    },
    finance: {
      title: 'Finance Calculator',
      subtitle: 'Estimate your monthly payment or vehicle price.',
      ratesFrom: 'Rates from',
      seeYourRate: 'Get your personalized rate in minutes.',
      yourCreditStaysUntouched: 'No impact to your credit score.',
      checkYourRateSubline: 'Complete a quick pre-qualification and explore options that fit your budget.',
      checkYourRate: 'Check my rate',
      aprDisclaimer:
        'APR starts at 4.99% for highly qualified buyers on select vehicles and terms up to 72 months. Rates and offers vary by credit profile, vehicle, lender, and market conditions. All financing is subject to lender approval.',
      paymentCalculator: 'Payment calculator',
      calcSubtitle: 'Estimate your monthly payment. Rates and terms subject to credit approval. Final terms may vary.',
      getPreQualified: 'Get pre-qualified',
      applyPageTitle: 'Get financed',
      applyPageLead: 'Take the next step toward your vehicle. Your secure application or partner flow can live here.',
      applyPagePlaceholder:
        'Lender integration or application form coming soon — your finance team can plug in a bank widget, DMS flow, or custom application here.',
      backToFinanceCalculator: 'Back to payment calculator',
      trustStripLabel: 'Financing benefits',
      trust: {
        softCredit: {
          title: 'Soft credit check',
          desc: 'Pre-qualification does not impact your credit score.',
        },
        fastDecisions: {
          title: 'Fast decisions',
          desc: 'Get a response in minutes, not days.',
        },
        flexibleTerms: {
          title: 'Flexible terms',
          desc: 'Choose a payment plan that fits your budget.',
        },
        trustedPartner: {
          title: 'Trusted partner',
          desc: 'Financing powered by Lendmark Financial Services.',
        },
      },
      partner: {
        badge: 'Financing partner — Lendmark Financial Services',
        eyebrow: 'Our first financing partner',
        title: 'Financing you can trust with Lendmark',
        lead: 'We have partnered with Lendmark Financial Services to help you get behind the wheel with competitive rates, a straightforward application, and support every step of the way.',
        learnMore: 'Visit Lendmark',
        logoAlt: 'Lendmark Financial Services',
        note: 'Lendmark Financial Services is a trusted consumer finance company serving communities nationwide.',
        stats: {
          years: 'Years of experience',
          locations: 'Branch locations',
          customers: 'Customers served',
        },
        benefits: {
          competitiveRates: {
            title: 'Competitive rates',
            desc: 'Options designed to fit a wide range of credit profiles.',
          },
          simpleProcess: {
            title: 'Simple process',
            desc: 'Apply online in minutes with a secure, guided application.',
          },
          driveSooner: {
            title: 'Drive sooner',
            desc: 'Get pre-qualified and shop inventory with confidence.',
          },
        },
      },
      how: {
        title: 'How financing works',
        lead: 'From application to delivery in four clear steps — powered by our Lendmark partnership.',
        steps: {
          apply: {
            title: 'Apply',
            desc: 'Complete our secure application with income and employment information. Processing typically takes a few minutes.',
          },
          checkRate: {
            title: 'Check your rate',
            desc: 'Pre-qualification is quick and will not affect your credit score. Lendmark provides estimated terms so you can shop with confidence.',
          },
          shop: {
            title: 'Shop with your budget',
            desc: 'Browse inventory within your approved range. Pre-approval is typically valid for 60 days.',
          },
          drive: {
            title: 'Finalize and drive',
            desc: 'Complete documentation online or in person. Schedule pickup or delivery at your convenience.',
          },
        },
      },
      cta: {
        title: 'Ready to get pre-qualified?',
        lead: 'Take the first step toward your next vehicle. It only takes a few minutes and won\'t affect your credit score.',
        note: 'Subject to credit approval. Terms and rates vary by applicant and vehicle.',
      },
      brochure: {
        eyebrow: 'Used car dealership',
        title: 'Why buyers choose us',
        points: {
          auctionSourcing: {
            title: 'Auction to Own Program & Vehicle Sourcing',
            desc: 'Tell us the make, model, and budget you want — we source it for a flat fee under $150. You keep every dollar saved.',
          },
          lendmark: {
            title: 'Lendmark Financial Services',
            desc: 'Our first financing partner — competitive rates and flexible terms nationwide.',
          },
          calculator: {
            title: 'Payment calculator built in',
            desc: 'Estimate your monthly payment before you pick a car. Adjust down payment, term, and APR in real time.',
          },
          shopBudget: {
            title: 'Shop with your budget',
            desc: 'Browse inventory knowing what you can afford. Pre-approval is typically valid for up to 60 days.',
          },
          softCredit: {
            title: 'Soft credit pre-qualification',
            desc: 'Check your rate in minutes with no impact to your credit score — shop with confidence before you commit.',
          },
          flexibleTerms: {
            title: 'Flexible loan terms',
            desc: 'Choose from 36 to 84 month terms. Find a monthly payment that fits your life, not the other way around.',
          },
          browseOnline: {
            title: 'Browse the lot from your couch',
            desc: 'Every vehicle listed online with photos, mileage, and price. Do your homework before you set foot on the lot.',
          },
          tradeIn: {
            title: 'Trade-in friendly',
            desc: 'Have a vehicle to trade? Apply its value toward your down payment and simplify your upgrade in one visit.',
          },
        },
      },
      prequal: {
        dealershipName: 'My Cars Lot',
        heroTitle: 'Start your financing request',
        benefit1: 'Private, secure application',
        benefit2: 'Our team follows up with you directly',
        benefit3: 'Explore options that may fit your budget',
        sectionBasics: "Let's start with the basics",
        shoppingAtPrefix: "You're shopping at",
        editStore: 'Edit location',
        changeStore: 'Change',
        storesLoading: 'Loading store locations…',
        storesFetchError: 'We could not load store locations. Please refresh or try again later.',
        noStoresConfigured: 'No store locations are available. Please contact the dealership.',
        selectStoreError: 'Please select a store.',
        desiredCarPrice: 'Desired car price',
        avgCarPriceHint: 'Our average car price is {{amount}}',
        downPayment: 'Down payment',
        continue: 'Continue',
        back: 'Back',
        sectionIdentity: 'Next, add your info to verify your identity',
        firstName: 'First name',
        lastName: 'Last name',
        dateOfBirth: 'Date of birth',
        email: 'Email',
        phone: 'Phone',
        phonePlaceholder: '(555) 123-4567',
        phoneDisclaimerStart:
          'Phone number required to pre-qualify and to provide you with updates.',
        textingTerms: 'Texting Terms',
        ssn: 'Social Security number',
        ssnDisclaimerStart: "This won't impact your credit score.",
        privacyPolicy: 'Privacy Policy',
        addCoBuyer: "Add co-buyer's information",
        coBuyerSectionTitle: "Co-buyer's information",
        removeCoBuyer: 'Remove co-buyer',
        sectionHousing: 'Now, add your housing and address info',
        streetAddress: 'Street address',
        aptOptional: 'Apt / suite / etc. (optional)',
        city: 'City',
        state: 'State',
        zipCode: 'ZIP code',
        zipPlaceholder: '12345 or 12345-6789',
        selectState: 'Select state',
        housingStatus: 'Housing status',
        moveInDate: 'Move-in date (MM/YYYY)',
        monthlyHousing: 'Monthly payment',
        selectHousing: 'Select housing status',
        housingRent: 'Rent',
        housingOwn: 'Own',
        housingFamily: 'Live with family',
        housingOther: 'Other',
        sectionIncome: 'Share your income to get your results',
        employmentStatus: 'Employment status',
        selectEmployment: 'Select employment status',
        empEmployed: 'Employed',
        empSelf: 'Self-employed',
        empUnemployed: 'Unemployed',
        empRetired: 'Retired',
        empStudent: 'Student',
        employerName: 'Employer name',
        employerNamePlaceholder: 'Company or business name',
        jobTitle: 'Job title',
        jobTitlePlaceholder: 'Your role',
        startDate: 'Start date',
        startDateHint: 'MM/YYYY',
        annualIncomeLabel: 'Your annual income (pre-tax)',
        monthlyIncomeLabel: 'Monthly income',
        estimatedAnnualLabel: 'Estimated annual income (pre-tax)',
        estimatedMonthlyLabel: 'Estimated monthly income',
        incomeTypeGroup: 'Income basis',
        incomeTypeSalary: 'Salary',
        incomeTypeHourly: 'Hourly',
        incomeTypeOther: 'Other',
        hourlyRateLabel: 'Hourly rate',
        hoursPerWeekLabel: 'Hours per week',
        addAdditionalIncome: 'Add additional income*',
        additionalIncomeLabel: 'Additional monthly income (optional)',
        additionalIncomeHint:
          'While not required, additional income could increase your chances of pre-qualification.',
        incomeFootnote:
          "*You don't have to reveal alimony, child support, or separate maintenance income if you don't want it considered as part of your pre-qualification request.",
        termsSectionTitle: 'Review the Terms & Conditions to continue',
        termsSectionSubtitle: 'Terms, Conditions, & eSign Consent for Kathan',
        termsCheckbox: 'I agree to these Terms and Conditions and eSign Consent.',
        termsRequired: 'Please accept the terms and eSign consent to submit your application.',
        submit: 'Submit application',
        submitting: 'Submitting…',
        submitError: 'We could not save your application. Please try again.',
        successTitle: 'Thanks — we received your information',
        successBody:
          'A finance specialist will follow up with next steps. Rates and approval are subject to lender verification.',
        invalidPhone: 'Enter a valid 10-digit U.S. phone number.',
        invalidSsn: 'Enter a valid 9-digit Social Security number.',
        invalidEmail: 'Enter a valid email address.',
        stepProgress: 'Step {{current}} of {{total}}',
      },
    },
    sellTrade: {
      title: 'Sell or Trade Your Car',
      getInstantOffer: 'Get Instant Offer',
      licensePlate: 'License Plate',
      vin: 'VIN',
      inStore: 'In-Store',
      heroTitle: "Your car has value. We'll show you how much.",
      heroSubtitle: 'No runaround—just a straight number you can use to sell or trade.',
      howToGetOffer: 'How to get your offer',
      receiveOfferInMinutes: 'Receive your estimated offer in minutes',
      offerSuccessMessage: "Got it. We'll run the numbers and get back to you with your offer.",
      licensePlatePlaceholder: 'License plate number',
      selectState: 'Select state',
      zipCode: 'ZIP Code',
      zipCodePlaceholder: 'ZIP code',
      getOffer: 'Get offer',
      runningNumbers: 'Running the numbers…',
      vinPlaceholder: 'VIN (17 characters)',
      preferStoreMessage: "Prefer to bring your car in? Stop by and we'll give you a number on the spot—no appointment required.",
      findOurLocation: 'Find our location',
      disclaimer: 'Estimated values are subject to in-person inspection and final appraisal.',
      estimateTitle: "Estimate your car's value",
      estimateSubtitle: 'Quick estimate. Final offer after we see your vehicle.',
      year: 'Year',
      make: 'Make',
      selectMake: 'Select make',
      model: 'Model',
      selectModel: 'Select model',
      mileage: 'Mileage',
      condition: 'Condition',
      excellent: 'Excellent',
      good: 'Good',
      fair: 'Fair',
      poor: 'Poor',
      estimatedValue: 'Estimated value',
      getYourOfferComingSoon: 'Get your offer (coming soon)',
      subjectToAppraisal: 'Subject to in-person appraisal.',
      whySellTitle: 'Why Sell or Trade With Us',
      whySellLead: 'We combine market-based valuations with a straightforward process so you get a fair offer and a smooth experience—whether you sell outright or apply your vehicle\'s value toward your next purchase.',
      fairPricingTitle: 'Fair, Transparent Pricing',
      fairPricingDesc: "Your offer is based on current market data. We don't lowball—you get a competitive number you can trust.",
      quickTurnaroundTitle: 'Quick Turnaround',
      quickTurnaroundDesc: 'Receive a preliminary offer in minutes. From appraisal to payment, we aim to complete the process within 24 hours when possible.',
      noObligationTitle: 'No Obligation',
      noObligationDesc: 'Get your free quote with zero commitment. You decide whether to accept—no pressure, no hidden fees.',
      tradeInValueTitle: 'Trade-In Value',
      tradeInValueDesc: "Use your vehicle's value as credit toward your next car. Reduce the amount you finance and often save on sales tax.",
      professionalAppraisalTitle: 'Professional Appraisal',
      professionalAppraisalDesc: "A brief, thorough inspection ensures your final offer accurately reflects your vehicle's condition and market value.",
      flexiblePaymentTitle: 'Flexible Payment Options',
      flexiblePaymentDesc: 'Choose how you get paid: check, direct deposit, or trade credit applied to your next purchase. Same-day payment available.',
      howItWorksTitle: 'How Selling or Trading Works',
      howItWorksLead: 'From initial quote to payment or trade credit in three clear steps.',
      step1Title: 'Get Your Preliminary Offer',
      step1Desc: 'Enter your vehicle information above using your license plate, VIN, or visit us in person. We\'ll provide a preliminary offer based on the details you provide.',
      step2Title: 'Schedule Your Appraisal',
      step2Desc: 'Book a free, no-obligation appraisal at your convenience. We can come to you or you can stop by our location. The appraisal typically takes about 30 minutes.',
      step3Title: 'Accept Your Offer & Get Paid',
      step3Desc: 'Once you accept our final offer, we handle the paperwork. Get paid the same day by check or direct deposit, or apply the value as trade credit toward your next vehicle.',
      whatSellersSay: 'What Our Sellers Say',
      joinThousands: 'Join thousands of satisfied customers',
      testimonial1: '"Sold my car in less than 24 hours! The process was incredibly smooth, and the price was better than what other dealers offered."',
      testimonial2: '"Used my trade-in value toward a new car. MyCarSlot made the entire process seamless. Highly recommend!"',
      testimonial3: '"No haggling, no stress. Got a fair price and instant payment. This is how selling a car should be!"',
      errorLicensePlate: 'Enter your license plate number',
      errorSelectState: 'Select your state',
      errorVin: 'Enter your vehicle VIN',
      errorVinLength: 'VIN must be {{length}} characters',
      errorZipRequired: 'Zip code is required',
      errorZipInvalid: 'Enter a valid 5- or 9-digit zip code',
      vinDecodeFailed: 'We couldn\'t find that VIN. Check the number and try again.',
    },
  },
  es: {
    home: {
      heroFindTitle: 'Encuentra tu auto',
      price: 'Precio',
      brand: 'Marca',
      type: 'Tipo',
      year: 'Año',
      any: 'Cualquiera',
      search: 'Buscar',
      heroTitle: 'Autos honestos.',
      heroTitleAccent: 'Tratos directos.',
      heroSlide2Title: 'Calidad en la que puede confiar.',
      heroSlide2Accent: 'Siempre.',
      heroSlide3Title: 'Precios transparentes.',
      heroSlide3Accent: 'Sin sorpresas.',
      heroTrust1: 'Financiamiento disponible',
      heroTrust2: 'Historial completo del vehículo',
      heroTrust3: 'Valores justos de canje',
      heroSubtitle: 'Vehículos de calidad seminuevos, precios transparentes y un servicio en el que puede confiar. Descubra {{count}}+ autos de {{brands}} marcas.',
      browseInventory: 'Ver inventario',
      soundOn: 'Activar música de fondo',
      soundOff: 'Desactivar música de fondo',
      contactUs: 'Contáctenos',
      trustedDealer: 'Concesionario de confianza',
      licensedTransparent: 'Licenciado y transparente',
      awardWinningService: 'Servicio de confianza',
      customerFirst: 'Experiencia centrada en el cliente',
      transparentPricing: 'Precios transparentes',
      noHiddenFees: 'Sin cargos ocultos',
      responsibleBuying: 'Compra responsable',
      qualityPreOwned: 'Seminuevos de calidad, inspeccionados',
      newArrivalsThisWeek: 'Novedades esta semana',
      vehiclesJustAdded: '{{count}}+ vehículos recién agregados',
      findOutMore: 'Saber más',
      financingAvailable: 'Financiamiento disponible',
      getPreApproved: 'Obtenga preaprobación en minutos',
      sellOrTrade: 'Venda o cambie su auto',
      instantValuation: 'Valuación al instante, precios justos',
      whatWeOffer: 'Lo que ofrecemos',
      fromBrowsingToFinancing: 'Desde la búsqueda hasta la financiación, lo acompañamos en cada paso.',
      seeMore: 'Ver más',
      browseInventoryDesc: 'Explore nuestra selección de vehículos seminuevos de calidad. Cada auto está inspeccionado y listo para su próximo viaje.',
      sellTradeTitle: 'Vender / Canje',
      sellTradeDesc: 'Obtenga una valuación al instante. Actualice a su próximo auto con un proceso simple y transparente.',
      financingTitle: 'Financiamiento',
      financingDesc: 'Tasas competitivas y plazos flexibles. Preaprobación en minutos para conducir a casa con confianza.',
      contactSupport: 'Contacto y soporte',
      contactSupportDesc: 'Visítenos, programe una prueba de manejo o pregunte lo que necesite. Estamos aquí para ayudarle a encontrar el auto indicado.',
      carsAvailable: 'Autos disponibles',
      carsSold: 'Autos vendidos',
      newArrivals: 'Novedades',
      brands: 'Marcas',
      mostViewedCars: 'Los autos más vistos en nuestro lote',
      shopByType: 'Comprar por tipo',
      popularBrands: 'Marcas populares',
      trustedManufacturers: 'Fabricantes de confianza en nuestra línea',
      available: 'disponible',
      comingSoon: 'Próximamente',
      financingOptions: 'Opciones de financiamiento',
      preApprovalTerms: 'Preaprobación en minutos, tasas competitivas, planes flexibles.',
      quickApproval: 'Aprobación rápida',
      competitiveRates: 'Tasas competitivas',
      flexiblePlans: 'Planes flexibles',
      tradeInService: 'Servicio de canje',
      tradeInDesc: 'Valuación al instante y precios justos de mercado para su vehículo actual.',
      instantValuationLabel: 'Valuación al instante',
      fairPricing: 'Precios justos',
      simpleProcess: 'Proceso simple',
    },
    common: {
      inventory: 'Inventario',
      finance: 'Finanzas',
      sellTrade: 'Vender/Canje',
      contact: 'Contacto',
      signIn: 'Iniciar sesión',
      saved: 'Guardados',
      searchPlaceholder: '¿Qué tipo de auto buscas?',
      prev: 'Anterior',
      next: 'Siguiente',
      goToSlide: 'Ir a diapositiva',
      skipToContent: 'Saltar al contenido principal',
      backToTop: 'Volver arriba',
    },
    profile: {
      signInToView: 'Inicie sesión para ver su perfil',
      personalInfo: 'Información personal',
      paymentMethods: 'Métodos de pago',
      documents: 'Documentos',
      security: 'Seguridad',
      notifications: 'Notificaciones',
      preferences: 'Preferencias',
      personalInformation: 'Información personal',
      paymentMethodsTitle: 'Métodos de pago',
      documentsTitle: 'Documentos',
      securitySettings: 'Configuración de seguridad',
      preferencesTitle: 'Preferencias',
      customizeExperience: 'Personalice su experiencia',
      loadingPreferences: 'Cargando preferencias...',
      appearance: 'Apariencia',
      theme: 'Tema',
      light: 'Claro',
      whiteBackground: 'Fondo blanco',
      dark: 'Oscuro',
      darkBackground: 'Fondo oscuro',
      languageAndRegion: 'Idioma y región',
      language: 'Idioma',
      timezone: 'Zona horaria',
      dateFormat: 'Formato de fecha',
      currency: 'Moneda',
      uploadDocuments: 'Subir documentos',
      yourDocuments: 'Sus documentos',
      chooseFiles: 'Elegir archivos',
      uploading: 'Subiendo...',
      person: 'Persona',
      addCard: 'Agregar tarjeta',
      adding: 'Agregando...',
      save: 'Guardar',
      cancel: 'Cancelar',
      remove: 'Eliminar',
    },
    footer: {
      companyDesc: 'Nos comprometemos a ofrecer soluciones automotrices excepcionales a través de nuestro inventario de vehículos seminuevos premium, estructuras de precios transparentes y un servicio al cliente incomparable.',
      shop: 'Tienda',
      browseInventory: 'Ver inventario',
      financingOptions: 'Opciones de financiamiento',
      sellYourCar: 'Venda su auto',
      tradeInValue: 'Valor de canje',
      company: 'Empresa',
      aboutUs: 'Sobre nosotros',
      contact: 'Contacto',
      careers: 'Carreras',
      blog: 'Blog',
      support: 'Soporte',
      faq: 'Preguntas frecuentes',
      shippingInfo: 'Información de envío',
      warranty: 'Garantía',
      returns: 'Devoluciones',
      customerFeedbackNps: 'Opinión del cliente',
      purchaseExperienceSurvey: 'Sugerir funciones y mejoras',
      legal: 'Legal',
      privacyPolicy: 'Política de privacidad',
      termsOfService: 'Términos de servicio',
      cookiesPolicy: 'Política de cookies',
      allRightsReserved: 'Todos los derechos reservados.',
    },
    auth: {
      welcome: 'Bienvenido',
      enterEmailToStart: 'Ingrese su correo para comenzar',
      email: 'Correo electrónico',
      emailPlaceholder: 'su@correo.com',
      continue: 'Continuar',
      checking: 'Verificando...',
      createAccount: 'Crear cuenta',
      completeProfile: 'Complete su perfil',
      firstName: 'Nombre',
      lastName: 'Apellido',
      phone: 'Teléfono',
      password: 'Contraseña',
      createPassword: 'Crear contraseña',
      minChars: '8+ caracteres',
      number: 'Número',
      letter: 'Letra',
      noSpaces: 'Sin espacios',
      createAccountButton: 'Crear cuenta',
      creating: 'Creando...',
      welcomeBack: 'Bienvenido de nuevo',
      signInToAccount: 'Inicie sesión en su cuenta',
      noAccount: '¿No tiene una cuenta?',
      createAnAccount: 'Crear una cuenta',
      enterPassword: 'Ingrese contraseña',
      keepSignedIn: 'Mantenerme conectado',
      forgotPassword: '¿Olvidó su contraseña?',
      twoFactorCode: 'Código de autenticación de dos factores',
      enter6DigitCode: 'Ingrese código de 6 dígitos',
      enterCodeFromApp: 'Ingrese el código de 6 dígitos de su aplicación de autenticación',
      verify: 'Verificar',
      signingIn: 'Iniciando sesión...',
      verifying: 'Verificando...',
      resetPassword: 'Restablecer contraseña',
      sendResetCode: 'Le enviaremos un código de restablecimiento',
      sendResetCodeButton: 'Enviar código',
      sending: 'Enviando...',
      newPassword: 'Nueva contraseña',
      enterCodeAndPassword: 'Ingrese código y nueva contraseña',
      resetCode: 'Código de restablecimiento',
      resetCodePlaceholder: 'Código de 6 dígitos',
      confirmPassword: 'Confirmar contraseña',
      confirmPasswordPlaceholder: 'Confirmar contraseña',
      resetPasswordButton: 'Restablecer contraseña',
      resetting: 'Restableciendo...',
    },
    carCard: {
      used: 'USADO',
      certified: 'CERTIFICADO',
      estimatedPrice: 'Precio estimado',
      priceTbd: 'Precio por definir',
      estimatedMo: 'Estimado',
      cashDown: '$0 de enganche',
      freeDelivery: 'Entrega gratuita',
      shipping: 'envío',
      deliveredBefore: 'Entregado antes del',
      calculatingShipping: 'Calculando envío...',
      compare: 'Comparar',
      addToWishlist: 'Agregar a favoritos',
      expectedLaunch: 'LANZAMIENTO PREVISTO:',
      priceDrop: 'Bajada de precio',
      was: 'era',
      vehicle: 'Vehículo',
      prequalCta: 'Obtenga una preaprobación para ver su pago mensual personalizado',
    },
    carDetails: {
      priceHistoryTitle: 'Historial de precios',
      priceWentDown: 'Precio reducido en',
      priceWentUp: 'Precio aumentado en',
    },
    cookie: {
      weUseCookies: 'Usamos cookies',
      cookieMessage: 'Usamos cookies para mejorar su experiencia, analizar el tráfico y personalizar el contenido. Al hacer clic en "Aceptar todo", acepta nuestro uso de cookies.',
      customize: 'Personalizar',
      rejectAll: 'Rechazar todo',
      acceptAll: 'Aceptar todo',
      cookiePreferences: 'Preferencias de cookies',
      managePreferences: 'Gestione sus preferencias de cookies.',
      essentialCookies: 'Cookies esenciales',
      essentialDesc: 'Necesarias para el funcionamiento del sitio. No se pueden desactivar.',
      analyticsCookies: 'Cookies de análisis',
      analyticsDesc: 'Nos ayudan a entender cómo interactúan los visitantes con el sitio.',
      marketingCookies: 'Cookies de marketing',
      marketingDesc: 'Para anuncios personalizados y seguimiento de campañas.',
      dataSharing: 'Compartir datos',
      dataSharingDesc: 'Permitir compartir sus datos con socios de confianza.',
      savePreferences: 'Guardar preferencias',
      saving: 'Guardando...',
    },
    notFound: {
      carNotFound: 'Auto no encontrado',
      carNotFoundMessage: 'El auto que busca no existe o ha sido eliminado.',
      backToInventory: 'Volver al inventario',
      pageNotFound: 'Página no encontrada',
      pageNotFoundMessage: 'La página que busca no existe.',
      goHome: 'Ir al inicio',
      goBackToCar: 'Volver a la página del auto',
    },
    contact: {
      pageEyebrow: 'Soporte',
      getInTouch: 'Contáctenos',
      getInTouchSub: '¿Preguntas sobre inventario, financiamiento o entrega? Elija la opción más rápida — o envíenos un mensaje y le responderemos en horario de tienda.',
      quickActionsTitle: 'Contáctenos como prefiera',
      chatAction: 'Chat en vivo',
      chatActionDesc: 'Respuestas rápidas mientras navega',
      phoneAction: 'Llámenos',
      faceTimeAction: 'FaceTime',
      faceTimeActionDesc: 'Dispositivos Apple · horario de tienda',
      emailAction: 'Correo',
      emailActionDesc: 'sales@mycarslot.com',
      faqAction: 'FAQ y ayuda',
      faqActionDesc: 'Preguntas frecuentes',
      formLead: 'Solemos responder el mismo día hábil en horario de tienda.',
      directions: 'Cómo llegar',
      contactInfo: 'Información de contacto',
      contactInfoText: 'Visitas sin cita bienvenidas. Programe una prueba de manejo en línea y tendremos su vehículo listo.',
      address: 'Dirección',
      phone: 'Teléfono',
      email: 'Correo electrónico',
      hours: 'Horario',
      sendMessage: 'Envíenos un mensaje',
      name: 'Nombre',
      subject: 'Asunto',
      message: 'Mensaje',
      send: 'Enviar',
      sending: 'Enviando...',
      successMessage: '¡Gracias! Su mensaje se ha enviado correctamente.',
      businessHours: 'Horario de atención',
      fullName: 'Nombre completo',
      selectSubject: 'Seleccione un asunto',
      generalInquiry: 'Consulta general',
      salesQuestion: 'Pregunta de ventas',
      serviceSupport: 'Servicio y soporte',
      tradeIn: 'Canje',
      other: 'Otro',
      messagePlaceholder: 'Cuéntenos en qué podemos ayudarle...',
      sendButton: 'Enviar mensaje',
      visitDealership: 'Visite nuestro concesionario',
    },
    faqSupport: {
      eyebrow: 'Centro de ayuda',
      heroTitle: 'FAQ y soporte',
      heroSubtitle: 'Encuentre respuestas sobre compra, financiamiento, venta, entrega y su cuenta — o contacte a nuestro equipo.',
      hubSubtitle: 'Elija un tema para ver preguntas frecuentes o contacte a nuestro equipo.',
      helpCenter: 'Centro de ayuda',
      searchPlaceholder: 'Buscar preguntas...',
      topicsLabel: 'Temas de FAQ',
      browseByTopic: 'Explorar por tema',
      popularLabel: 'Preguntas populares',
      quickLinksLabel: 'Enlaces rápidos',
      hoursTitle: 'Horario de soporte',
      hoursWeekday: 'Lun–Vie: 9:00 AM – 7:00 PM',
      hoursSaturday: 'Sáb: 9:00 AM – 6:00 PM · Dom: 10:00 AM – 5:00 PM',
      allTopics: 'Todos los temas',
      resultCount: '{{count}} preguntas',
      noResults: 'No se encontraron preguntas',
      noResultsHint: 'Pruebe otro término o contáctenos para ayuda personalizada.',
      backToTopic: 'Volver a {{topic}}',
      moreInTopic: 'Más en {{topic}}',
      expertTitle: 'Hable con un experto',
      expertMessage: 'Envíenos un mensaje y le ayudaremos a encontrar el auto correcto o responder sus preguntas.',
      expertPhone: 'Llame al (228) 202-9614 para ayuda con compra, venta o entrega.',
      categoryDesc: {
        buying: 'Inventario, Auction to Own, historial y estándares de calidad',
        finding: 'Cómo funciona, presupuesto, comparar y búsquedas guardadas',
        financing: 'Precalificación, Lendmark, pagos y crédito',
        payments: 'Fechas de pago, métodos, pago anticipado y pagos atrasados',
        selling: 'Ofertas de venta, canje y aplicar su valor',
        delivery: 'Entrega a domicilio, recogida y cotizaciones',
        storeVisit: 'Pruebas de manejo, citas y qué traer',
        warranty: 'Garantía de fábrica, planes extendidos y cobertura',
        sourcing: 'Auction to Own, tarifas de búsqueda y plazos',
        account: 'Favoritos, búsquedas guardadas y citas',
        security: 'Seguridad de cuenta, privacidad y contacto sospechoso',
        recalls: 'Retiros abiertos, reparaciones y verificación',
        afterPurchase: 'Devoluciones, documentos y soporte postventa',
      },
      categories: {
        buying: 'Comprar un auto',
        finding: 'Encontrar un auto',
        financing: 'Financiamiento',
        payments: 'Realizar pagos',
        selling: 'Vender y canje',
        delivery: 'Entrega y recogida',
        storeVisit: 'Visitas y pruebas de manejo',
        warranty: 'Garantías y protección',
        sourcing: 'Auction to Own y búsqueda',
        account: 'Su cuenta',
        security: 'Seguridad y privacidad',
        recalls: 'Retiros de seguridad',
        afterPurchase: 'Después de comprar',
      },
      questions: {
        buying: {
          browseInventory: {
            q: '¿Cómo navego y comparo vehículos en línea?',
            a: 'Abra Inventario para filtrar por marca, modelo, precio, tipo, kilometraje y más. Guarde favoritos, compare listados y programe una visita cuando esté listo.',
          },
          testDrive: {
            q: '¿Puedo programar una prueba de manejo?',
            a: 'Sí. Elija un vehículo y reserve una cita en línea, o contáctenos para coordinar un horario. Las visitas sin cita son bienvenidas según disponibilidad.',
          },
          auctionToOwn: {
            q: '¿Qué es el programa Auction to Own?',
            a: 'Indíquenos marca, modelo y presupuesto y buscaremos el vehículo en nuestra red de subastas por una tarifa fija de menos de $150. Usted se queda con el ahorro.',
          },
          vehicleHistory: {
            q: '¿Los vehículos incluyen historial e inspección?',
            a: 'Ofrecemos información transparente y podemos revisar reportes de historial, condición e inspección de cualquier vehículo que considere.',
          },
        },
        financing: {
          preQual: {
            q: '¿Cómo funciona la precalificación?',
            a: 'Use Ver mi tasa en Finanzas o el flujo Get Financed. La precalificación es rápida y le da términos estimados para comprar con confianza.',
          },
          lendmark: {
            q: '¿Quién ofrece financiamiento?',
            a: 'Nos asociamos con Lendmark Financial Services para tasas competitivas a nivel nacional. La aprobación final depende de su perfil crediticio y el vehículo.',
          },
          calculator: {
            q: '¿Puedo estimar mi pago mensual antes de aplicar?',
            a: 'Sí. Use la calculadora en la página de Finanzas para ajustar precio, enganche, plazo y APR y ver pagos estimados en tiempo real.',
          },
          softCredit: {
            q: '¿Consultar mi tasa afecta mi crédito?',
            a: 'La precalificación usa una consulta suave que no afecta su puntaje. Una consulta dura puede ocurrir solo si continúa con una solicitud completa.',
          },
        },
        selling: {
          instantOffer: {
            q: '¿Cómo obtengo una oferta de venta o canje?',
            a: 'Visite Vender/Canje e ingrese placa, VIN o datos del vehículo. Le daremos una oferta competitiva para aceptar o aplicar a su próxima compra.',
          },
          tradeInProcess: {
            q: '¿Cómo funciona el proceso de canje?',
            a: 'Tras recibir una oferta, traiga su vehículo para verificación. Confirmamos condición y documentos, luego aplicamos el valor a su compra o pago.',
          },
          payDifference: {
            q: '¿Puedo canjear y financiar la diferencia?',
            a: 'Sí. El valor del canje puede reducir lo que financia. Nuestro equipo ayuda a estructurar un pago que se ajuste a su presupuesto.',
          },
        },
        delivery: {
          deliveryOptions: {
            q: '¿Ofrecen entrega a domicilio?',
            a: 'La entrega puede estar disponible según su ubicación y el vehículo. Contáctenos con su código postal para opciones y cotización.',
          },
          deliveryCost: {
            q: '¿Cuánto cuesta la entrega?',
            a: 'El precio varía por distancia, vehículo y nivel de servicio. Algunos listados pueden incluir promociones — pregúntenos sin compromiso.',
          },
          pickup: {
            q: '¿Puedo recoger en el concesionario?',
            a: 'Sí. Muchos clientes eligen recoger en Ocean Springs. Preparamos su documentación y le guiamos en la entrega.',
          },
        },
        account: {
          createAccount: {
            q: '¿Necesito cuenta para comprar?',
            a: 'Puede navegar sin cuenta. Crear una cuenta gratuita le permite guardar favoritos, búsquedas, citas y progreso de financiamiento.',
          },
          appointments: {
            q: '¿Dónde veo mis citas?',
            a: 'Inicie sesión y abra Citas en su menú de cuenta para revisar, reprogramar o cancelar visitas.',
          },
          wishlist: {
            q: '¿Cómo funcionan favoritos y búsquedas guardadas?',
            a: 'Use el ícono de corazón para guardar autos y Guardar búsqueda en Inventario para recibir alertas. Ambos requieren cuenta gratuita.',
          },
        },
        afterPurchase: {
          warranty: {
            q: '¿Qué garantía está disponible?',
            a: 'La cobertura varía por vehículo e puede incluir garantía de fábrica restante o planes extendidos opcionales. Pregúntenos antes de comprar.',
          },
          returns: {
            q: '¿Qué pasa si algo no está bien después de comprar?',
            a: 'Contáctenos de inmediato. Revisaremos su situación y buscaremos una resolución justa según la ley y nuestras políticas.',
          },
          serviceHelp: {
            q: '¿A quién contacto después de la compra?',
            a: 'Llame, escriba o use el formulario de contacto. Para financiamiento, incluya detalles de su solicitud para conectarle con el especialista correcto.',
          },
        },
      },
      support: {
        title: '¿Aún necesita ayuda?',
        subtitle: 'Nuestros especialistas pueden guiarle — por teléfono, correo o mensaje.',
        callTitle: 'Llámenos',
        callDesc: '(228) 202-9614 · Lun–Sáb',
        emailTitle: 'Escríbanos',
        emailDesc: 'sales@mycarslot.com',
        contactTitle: 'Enviar mensaje',
        contactDesc: 'Normalmente respondemos en un día hábil.',
        contactCta: 'Contáctenos',
        inventoryCta: 'Ver inventario',
        financeCta: 'Explorar financiamiento',
        sellCta: 'Vender o canjear',
      },
    },
    inventory: {
      compareCars: 'Comparar autos',
      saveSearch: 'Guardar búsqueda',
      filters: 'Filtros',
      sortBy: 'Ordenar por',
      noCars: 'Ningún vehículo coincide con sus filtros.',
      clearFilters: 'Limpiar filtros',
      clearAll: 'Limpiar todo',
      results: 'resultados',
      filter: 'Filtro',
      activeFiltersLabel: 'Filtros activos:',
      saveSearchTitle: 'Guardar búsqueda actual',
      share: 'Compartir',
      linkCopied: '¡Enlace copiado!',
      shareFilteredView: 'Compartir vista filtrada',
      copied: '¡Copiado!',
      comparingItems: 'Comparando {{current}} de {{max}}',
      removeFromComparison: 'Quitar de la comparación',
      noCarsMatch: 'Ningún auto coincide con sus filtros',
      tryClearingFilters: 'Intente limpiar filtros o ajustar criterios',
      totalCarsInDb: 'Total de autos en la base:',
      availableCars: 'Autos disponibles:',
      pleaseLogInToSave: 'Inicie sesión para guardar su búsqueda',
    },
    finance: { title: 'Calculadora de financiamiento', subtitle: 'Estime su pago mensual o precio del vehículo.', ratesFrom: 'Tasas desde', seeYourRate: 'Vea su tasa en minutos.', yourCreditStaysUntouched: 'Su puntaje de crédito no se ve afectado.', checkYourRateSubline: 'Verifique su tasa en menos de 2 minutos. Sin impacto en su crédito.', checkYourRate: 'Ver su tasa', paymentCalculator: 'Calculadora de pagos', calcSubtitle: 'Estime su pago mensual. Tasas y plazos sujetos a aprobación de crédito.', getPreQualified: 'Obtener preaprobación', applyPageTitle: 'Finánciate', applyPageLead: 'Da el siguiente paso hacia tu vehículo. Aquí irá tu solicitud segura o el flujo de tu socio.', applyPagePlaceholder: 'Próximamente: integración con prestamista o formulario. Su equipo puede conectar un widget bancario, flujo DMS o solicitud personalizada.', backToFinanceCalculator: 'Volver a la calculadora de pagos' },
    sellTrade: {
      title: 'Venda o canje su auto',
      getInstantOffer: 'Obtener oferta al instante',
      licensePlate: 'Placas',
      vin: 'VIN',
      inStore: 'En tienda',
      heroTitle: 'Su auto tiene valor. Le mostramos cuánto.',
      heroSubtitle: 'Sin rodeos: un número claro para vender o canjear.',
      howToGetOffer: 'Cómo obtener su oferta',
      receiveOfferInMinutes: 'Reciba su oferta estimada en minutos',
      offerSuccessMessage: 'Listo. Calcularemos y le enviaremos su oferta.',
      licensePlatePlaceholder: 'Número de placas',
      selectState: 'Seleccione estado',
      zipCode: 'Código postal',
      zipCodePlaceholder: 'Código postal',
      getOffer: 'Obtener oferta',
      runningNumbers: 'Calculando…',
      vinPlaceholder: 'VIN (17 caracteres)',
      preferStoreMessage: '¿Prefiere traer su auto? Visítenos y le damos un número al momento, sin cita.',
      findOurLocation: 'Encuentre nuestra ubicación',
      disclaimer: 'Los valores estimados están sujetos a inspección en persona y avalúo final.',
      estimateTitle: 'Estime el valor de su auto',
      estimateSubtitle: 'Estimación rápida. Oferta final después de ver su vehículo.',
      year: 'Año',
      make: 'Marca',
      selectMake: 'Seleccione marca',
      model: 'Modelo',
      selectModel: 'Seleccione modelo',
      mileage: 'Kilometraje',
      condition: 'Condición',
      excellent: 'Excelente',
      good: 'Bueno',
      fair: 'Regular',
      poor: 'Pobre',
      estimatedValue: 'Valor estimado',
      getYourOfferComingSoon: 'Obtenga su oferta (próximamente)',
      subjectToAppraisal: 'Sujeto a avalúo en persona.',
      whySellTitle: 'Por qué vender o canjear con nosotros',
      whySellLead: 'Combinamos valoraciones de mercado con un proceso claro para que obtenga una oferta justa y una experiencia fluida, ya sea que venda o use el valor hacia su próximo auto.',
      fairPricingTitle: 'Precios justos y transparentes',
      fairPricingDesc: 'Su oferta se basa en datos de mercado actuales. No regateamos: obtiene un número competitivo de confianza.',
      quickTurnaroundTitle: 'Respuesta rápida',
      quickTurnaroundDesc: 'Reciba una oferta preliminar en minutos. Nuestro objetivo es completar el proceso en 24 horas cuando sea posible.',
      noObligationTitle: 'Sin compromiso',
      noObligationDesc: 'Obtenga su cotización gratis sin compromiso. Usted decide si aceptar; sin presión ni cargos ocultos.',
      tradeInValueTitle: 'Valor de canje',
      tradeInValueDesc: 'Use el valor de su vehículo como crédito para su próximo auto. Reduzca el monto a financiar y ahorre en impuestos.',
      professionalAppraisalTitle: 'Avalúo profesional',
      professionalAppraisalDesc: 'Una inspección breve y minuciosa asegura que su oferta final refleje la condición y el valor de mercado de su vehículo.',
      flexiblePaymentTitle: 'Opciones de pago flexibles',
      flexiblePaymentDesc: 'Elija cómo recibir el pago: cheque, depósito directo o crédito de canje para su próxima compra. Pago el mismo día disponible.',
      howItWorksTitle: 'Cómo funciona vender o canjear',
      howItWorksLead: 'Desde la cotización inicial hasta el pago o crédito de canje en tres pasos.',
      step1Title: 'Obtenga su oferta preliminar',
      step1Desc: 'Ingrese los datos de su vehículo arriba (placas, VIN o visítenos). Le daremos una oferta preliminar según los datos que proporcione.',
      step2Title: 'Programe su avalúo',
      step2Desc: 'Reserve un avalúo gratuito y sin compromiso. Podemos ir a usted o puede visitarnos. El avalúo suele tomar unos 30 minutos.',
      step3Title: 'Acepte su oferta y reciba el pago',
      step3Desc: 'Al aceptar nuestra oferta final, nosotros nos encargamos del papeleo. Reciba el pago el mismo día por cheque o depósito, o aplique el valor como crédito de canje.',
      whatSellersSay: 'Lo que dicen nuestros vendedores',
      joinThousands: 'Únase a miles de clientes satisfechos',
      testimonial1: '"¡Vendí mi auto en menos de 24 horas! El proceso fue muy fluido y el precio mejor que en otros concesionarios."',
      testimonial2: '"Usé el valor de mi canje para un auto nuevo. MyCarSlot hizo todo el proceso muy fácil. ¡Lo recomiendo!"',
      testimonial3: '"Sin regateos ni estrés. Obtuve un precio justo y pago al instante. ¡Así debería ser vender un auto!"',
      errorLicensePlate: 'Ingrese el número de placas',
      errorSelectState: 'Seleccione su estado',
      errorVin: 'Ingrese el VIN del vehículo',
      errorVinLength: 'El VIN debe tener {{length}} caracteres',
      errorZipRequired: 'El código postal es obligatorio',
      errorZipInvalid: 'Ingrese un código postal de 5 o 9 dígitos válido',
    },
  },
  fr: {
    home: {
      heroFindTitle: 'Trouvez votre voiture',
      price: 'Prix',
      brand: 'Marque',
      type: 'Type',
      year: 'Année',
      any: 'Tout',
      search: 'Rechercher',
      heroTitle: 'Des voitures honnêtes.',
      heroTitleAccent: 'Des offres transparentes.',
      heroSlide2Title: 'Une qualité de confiance.',
      heroSlide2Accent: 'À chaque fois.',
      heroSlide3Title: 'Prix transparents.',
      heroSlide3Accent: 'Sans surprise.',
      heroTrust1: 'Financement disponible',
      heroTrust2: 'Historique véhicule complet',
      heroTrust3: 'Reprise équitable',
      heroSubtitle: 'Des véhicules d\'occasion de qualité, des prix transparents et un service de confiance. Découvrez {{count}}+ voitures de {{brands}} marques.',
      browseInventory: 'Parcourir le stock',
      soundOn: 'Activer la musique de fond',
      soundOff: 'Désactiver la musique de fond',
      contactUs: 'Nous contacter',
      trustedDealer: 'Concessionnaire de confiance',
      licensedTransparent: 'Agréé et transparent',
      awardWinningService: 'Service de confiance',
      customerFirst: 'Expérience client d\'abord',
      transparentPricing: 'Prix transparents',
      noHiddenFees: 'Pas de frais cachés',
      responsibleBuying: 'Achat responsable',
      qualityPreOwned: 'Occasions de qualité, contrôlées',
      newArrivalsThisWeek: 'Nouveautés cette semaine',
      vehiclesJustAdded: '{{count}}+ véhicules ajoutés',
      findOutMore: 'En savoir plus',
      financingAvailable: 'Financement disponible',
      getPreApproved: 'Pré-approuvé en quelques minutes',
      sellOrTrade: 'Vendez ou échangez votre voiture',
      instantValuation: 'Évaluation instantanée, prix justes',
      whatWeOffer: 'Ce que nous offrons',
      fromBrowsingToFinancing: 'De la recherche au financement, nous vous accompagnons à chaque étape.',
      seeMore: 'Voir plus',
      browseInventoryDesc: 'Parcourez notre sélection de véhicules d\'occasion de qualité. Chaque voiture est contrôlée et prête pour votre prochain trajet.',
      sellTradeTitle: 'Vente / Reprise',
      sellTradeDesc: 'Obtenez une évaluation instantanée. Passez à votre prochaine voiture avec un processus simple et transparent.',
      financingTitle: 'Financement',
      financingDesc: 'Taux compétitifs et conditions flexibles. Pré-approbation en minutes pour repartir en confiance.',
      contactSupport: 'Contact et support',
      contactSupportDesc: 'Rendez-nous visite, réservez un essai ou posez vos questions. Nous vous aidons à trouver la bonne voiture.',
      carsAvailable: 'Voitures disponibles',
      carsSold: 'Voitures vendues',
      newArrivals: 'Nouveautés',
      brands: 'Marques',
      mostViewedCars: 'Voitures les plus vues',
      shopByType: 'Acheter par type',
      popularBrands: 'Marques populaires',
      trustedManufacturers: 'Fabricants de confiance',
      available: 'disponible',
      comingSoon: 'Bientôt disponible',
      financingOptions: 'Options de financement',
      preApprovalTerms: 'Pré-approbation en minutes, taux compétitifs, plans flexibles.',
      quickApproval: 'Approbation rapide',
      competitiveRates: 'Taux compétitifs',
      flexiblePlans: 'Plans flexibles',
      tradeInService: 'Service de reprise',
      tradeInDesc: 'Évaluation instantanée et prix du marché équitables pour votre véhicule actuel.',
      instantValuationLabel: 'Évaluation instantanée',
      fairPricing: 'Prix justes',
      simpleProcess: 'Processus simple',
    },
    common: {
      inventory: 'Inventaire',
      finance: 'Finance',
      sellTrade: 'Vente/Reprise',
      contact: 'Contact',
      signIn: 'Connexion',
      saved: 'Enregistrés',
      searchPlaceholder: 'Quel type de véhicule cherchez-vous?',
      prev: 'Précédent',
      next: 'Suivant',
      goToSlide: 'Aller à la diapositive',
      skipToContent: 'Aller au contenu principal',
      backToTop: 'Retour en haut',
    },
    profile: {
      signInToView: 'Connectez-vous pour voir votre profil',
      personalInfo: 'Informations personnelles',
      paymentMethods: 'Moyens de paiement',
      documents: 'Documents',
      security: 'Sécurité',
      notifications: 'Notifications',
      preferences: 'Préférences',
      personalInformation: 'Informations personnelles',
      paymentMethodsTitle: 'Moyens de paiement',
      documentsTitle: 'Documents',
      securitySettings: 'Paramètres de sécurité',
      preferencesTitle: 'Préférences',
      customizeExperience: 'Personnalisez votre expérience',
      loadingPreferences: 'Chargement des préférences...',
      appearance: 'Apparence',
      theme: 'Thème',
      light: 'Clair',
      whiteBackground: 'Fond blanc',
      dark: 'Sombre',
      darkBackground: 'Fond sombre',
      languageAndRegion: 'Langue et région',
      language: 'Langue',
      timezone: 'Fuseau horaire',
      dateFormat: 'Format de date',
      currency: 'Devise',
      uploadDocuments: 'Télécharger des documents',
      yourDocuments: 'Vos documents',
      chooseFiles: 'Choisir des fichiers',
      uploading: 'Téléchargement...',
      person: 'Personne',
      addCard: 'Ajouter une carte',
      adding: 'Ajout en cours...',
      save: 'Enregistrer',
      cancel: 'Annuler',
      remove: 'Supprimer',
    },
    footer: {
      companyDesc: 'Nous nous engageons à offrir des solutions automobiles exceptionnelles grâce à notre inventaire de véhicules d\'occasion premium, des prix transparents et un service client inégalé.',
      shop: 'Boutique',
      browseInventory: 'Parcourir l\'inventaire',
      financingOptions: 'Options de financement',
      sellYourCar: 'Vendre votre véhicule',
      tradeInValue: 'Valeur de reprise',
      company: 'Entreprise',
      aboutUs: 'À propos',
      contact: 'Contact',
      careers: 'Carrières',
      blog: 'Blog',
      support: 'Support',
      faq: 'FAQ',
      shippingInfo: 'Livraison',
      warranty: 'Garantie',
      returns: 'Retours',
      customerFeedbackNps: 'Votre avis',
      purchaseExperienceSurvey: 'Idées de fonctions et d’interface',
      legal: 'Mentions légales',
      privacyPolicy: 'Politique de confidentialité',
      termsOfService: 'Conditions d\'utilisation',
      cookiesPolicy: 'Politique des cookies',
      allRightsReserved: 'Tous droits réservés.',
    },
    auth: {
      welcome: 'Bienvenue',
      enterEmailToStart: 'Entrez votre e-mail pour commencer',
      email: 'E-mail',
      emailPlaceholder: 'votre@email.com',
      continue: 'Continuer',
      checking: 'Vérification...',
      createAccount: 'Créer un compte',
      completeProfile: 'Complétez votre profil',
      firstName: 'Prénom',
      lastName: 'Nom',
      phone: 'Téléphone',
      password: 'Mot de passe',
      createPassword: 'Créer un mot de passe',
      minChars: '8+ caractères',
      number: 'Chiffre',
      letter: 'Lettre',
      noSpaces: 'Pas d\'espaces',
      createAccountButton: 'Créer un compte',
      creating: 'Création...',
      welcomeBack: 'Bon retour',
      signInToAccount: 'Connectez-vous à votre compte',
      noAccount: 'Vous n\'avez pas de compte ?',
      createAnAccount: 'Créer un compte',
      enterPassword: 'Entrez le mot de passe',
      keepSignedIn: 'Rester connecté',
      forgotPassword: 'Mot de passe oublié ?',
      twoFactorCode: 'Code d\'authentification à deux facteurs',
      enter6DigitCode: 'Code à 6 chiffres',
      enterCodeFromApp: 'Entrez le code à 6 chiffres de votre application d\'authentification',
      verify: 'Vérifier',
      signingIn: 'Connexion...',
      verifying: 'Vérification...',
      resetPassword: 'Réinitialiser le mot de passe',
      sendResetCode: 'Nous vous enverrons un code',
      sendResetCodeButton: 'Envoyer le code',
      sending: 'Envoi...',
      newPassword: 'Nouveau mot de passe',
      enterCodeAndPassword: 'Entrez le code et le nouveau mot de passe',
      resetCode: 'Code de réinitialisation',
      resetCodePlaceholder: 'Code à 6 chiffres',
      confirmPassword: 'Confirmer le mot de passe',
      confirmPasswordPlaceholder: 'Confirmer le mot de passe',
      resetPasswordButton: 'Réinitialiser le mot de passe',
      resetting: 'Réinitialisation...',
    },
    carCard: {
      used: 'OCCASION',
      certified: 'CERTIFIÉ',
      estimatedPrice: 'Prix estimé',
      priceTbd: 'Prix à définir',
      estimatedMo: 'Estimé',
      cashDown: '0 € d\'acompte',
      freeDelivery: 'Livraison gratuite',
      shipping: 'livraison',
      deliveredBefore: 'Livré avant',
      calculatingShipping: 'Calcul de la livraison...',
      compare: 'Comparer',
      addToWishlist: 'Ajouter aux favoris',
      expectedLaunch: 'LANCEMENT PRÉVU :',
      priceDrop: 'Baisse de prix',
      was: 'était',
      vehicle: 'Véhicule',
      prequalCta: 'Préqualifiez-vous pour voir votre paiement mensuel personnalisé',
    },
    carDetails: {
      priceHistoryTitle: 'Historique des prix',
      priceWentDown: 'Prix réduit de',
      priceWentUp: 'Prix augmenté de',
    },
    cookie: {
      weUseCookies: 'Nous utilisons des cookies',
      cookieMessage: 'Nous utilisons des cookies pour améliorer votre navigation, analyser le trafic et personnaliser le contenu. En cliquant sur "Tout accepter", vous acceptez notre utilisation des cookies.',
      customize: 'Personnaliser',
      rejectAll: 'Tout refuser',
      acceptAll: 'Tout accepter',
      cookiePreferences: 'Préférences de cookies',
      managePreferences: 'Gérez vos préférences de cookies.',
      essentialCookies: 'Cookies essentiels',
      essentialDesc: 'Requis pour le fonctionnement du site. Ils ne peuvent pas être désactivés.',
      analyticsCookies: 'Cookies d\'analyse',
      analyticsDesc: 'Nous aident à comprendre comment les visiteurs utilisent le site.',
      marketingCookies: 'Cookies marketing',
      marketingDesc: 'Pour la publicité personnalisée et le suivi des campagnes.',
      dataSharing: 'Partage de données',
      dataSharingDesc: 'Nous autoriser à partager vos données avec des partenaires de confiance.',
      savePreferences: 'Enregistrer les préférences',
      saving: 'Enregistrement...',
    },
    notFound: {
      carNotFound: 'Véhicule introuvable',
      carNotFoundMessage: 'Le véhicule que vous recherchez n\'existe pas ou a été retiré.',
      backToInventory: 'Retour à l\'inventaire',
      pageNotFound: 'Page introuvable',
      pageNotFoundMessage: 'La page que vous recherchez n\'existe pas.',
      goHome: 'Accueil',
      goBackToCar: 'Retour à la fiche véhicule',
    },
    contact: {
      pageEyebrow: 'Assistance',
      getInTouch: 'Contactez-nous',
      getInTouchSub: 'Des questions sur l\'inventaire, le financement ou la livraison ? Choisissez l\'option la plus rapide — ou envoyez-nous un message pendant nos heures d\'ouverture.',
      quickActionsTitle: 'Nous joindre',
      chatAction: 'Chat en direct',
      chatActionDesc: 'Réponses rapides pendant votre visite',
      phoneAction: 'Appelez-nous',
      faceTimeAction: 'FaceTime',
      faceTimeActionDesc: 'Appareils Apple · heures d\'ouverture',
      emailAction: 'E-mail',
      emailActionDesc: 'sales@mycarslot.com',
      faqAction: 'FAQ et aide',
      faqActionDesc: 'Questions fréquentes',
      formLead: 'Nous répondons généralement le jour ouvrable pendant nos heures d\'ouverture.',
      directions: 'Itinéraire',
      contactInfo: 'Coordonnées',
      contactInfoText: 'Sans rendez-vous bienvenus. Réservez un essai en ligne et nous préparerons votre véhicule.',
      address: 'Adresse',
      phone: 'Téléphone',
      email: 'E-mail',
      hours: 'Horaires',
      sendMessage: 'Envoyez-nous un message',
      name: 'Nom',
      subject: 'Sujet',
      message: 'Message',
      send: 'Envoyer',
      sending: 'Envoi...',
      successMessage: 'Merci ! Votre message a bien été envoyé.',
      businessHours: 'Heures d\'ouverture',
      fullName: 'Nom complet',
      selectSubject: 'Choisir un sujet',
      generalInquiry: 'Question générale',
      salesQuestion: 'Question vente',
      serviceSupport: 'Service et support',
      tradeIn: 'Reprise',
      other: 'Autre',
      messagePlaceholder: 'Dites-nous comment nous pouvons vous aider...',
      sendButton: 'Envoyer le message',
      visitDealership: 'Visitez notre concession',
    },
    faqSupport: {
      eyebrow: 'Centre d\'aide',
      heroTitle: 'FAQ et support',
      heroSubtitle: 'Réponses sur l\'achat, le financement, la vente, la livraison et votre compte — ou contactez notre équipe.',
      hubSubtitle: 'Choisissez un sujet pour parcourir les questions ou contactez notre équipe.',
      helpCenter: 'Centre d\'aide',
      searchPlaceholder: 'Rechercher une question...',
      topicsLabel: 'Sujets FAQ',
      browseByTopic: 'Parcourir par sujet',
      popularLabel: 'Questions populaires',
      quickLinksLabel: 'Liens rapides',
      hoursTitle: 'Heures de support',
      hoursWeekday: 'Lun–Ven : 9h00 – 19h00',
      hoursSaturday: 'Sam : 9h00 – 18h00 · Dim : 10h00 – 17h00',
      allTopics: 'Tous les sujets',
      resultCount: '{{count}} questions',
      noResults: 'Aucune question trouvée',
      noResultsHint: 'Essayez un autre terme ou contactez-nous pour une aide personnalisée.',
      backToTopic: 'Retour à {{topic}}',
      moreInTopic: 'Plus dans {{topic}}',
      expertTitle: 'Parlez à un expert',
      expertMessage: 'Envoyez-nous un message pour trouver la bonne voiture ou obtenir des réponses.',
      expertPhone: 'Appelez le (228) 202-9614 pour l\'achat, la vente ou la livraison.',
      categoryDesc: {
        buying: 'Inventaire, Auction to Own, historique et normes de qualité',
        finding: 'Fonctionnement, budget, comparaison et recherches enregistrées',
        financing: 'Préqualification, Lendmark, paiements et crédit',
        payments: 'Échéances, modes de paiement, remboursement anticipé',
        selling: 'Offres de vente, reprise et application de la valeur',
        delivery: 'Livraison, retrait et devis d\'expédition',
        storeVisit: 'Essais, rendez-vous et documents à apporter',
        warranty: 'Garantie constructeur, extensions et couverture',
        sourcing: 'Auction to Own, frais de sourcing et délais',
        account: 'Favoris, recherches enregistrées et rendez-vous',
        security: 'Sécurité du compte, confidentialité et arnaques',
        recalls: 'Rappels ouverts, réparations et vérification',
        afterPurchase: 'Retours, documents et support après achat',
      },
      categories: {
        buying: 'Acheter une voiture',
        finding: 'Trouver une voiture',
        financing: 'Financement',
        payments: 'Effectuer des paiements',
        selling: 'Vente et reprise',
        delivery: 'Livraison et retrait',
        storeVisit: 'Visites et essais routiers',
        warranty: 'Garanties et protection',
        sourcing: 'Auction to Own et sourcing',
        account: 'Votre compte',
        security: 'Sécurité et confidentialité',
        recalls: 'Rappels de sécurité',
        afterPurchase: 'Après l\'achat',
      },
      questions: {
        buying: {
          browseInventory: {
            q: 'Comment parcourir et comparer les véhicules en ligne ?',
            a: 'Ouvrez Inventaire pour filtrer par marque, modèle, prix, type et kilométrage. Enregistrez vos favoris, comparez les annonces et planifiez une visite.',
          },
          testDrive: {
            q: 'Puis-je planifier un essai routier ?',
            a: 'Oui. Choisissez un véhicule et réservez en ligne, ou contactez-nous pour fixer un horaire. Les visites sans rendez-vous sont possibles selon disponibilité.',
          },
          auctionToOwn: {
            q: 'Qu\'est-ce que le programme Auction to Own ?',
            a: 'Indiquez marque, modèle et budget et nous sourçons le véhicule via nos enchères pour des frais fixes inférieurs à 150 $. Vous gardez l\'économie réalisée.',
          },
          vehicleHistory: {
            q: 'Les véhicules incluent-ils l\'historique et l\'inspection ?',
            a: 'Nous fournissons des informations transparentes et pouvons détailler rapports d\'historique, état et inspection pour tout véhicule.',
          },
        },
        financing: {
          preQual: {
            q: 'Comment fonctionne la préqualification ?',
            a: 'Utilisez Vérifier mon taux sur Finance ou le parcours Get Financed. La préqualification est rapide et donne des conditions estimées pour acheter en confiance.',
          },
          lendmark: {
            q: 'Qui propose le financement auto ?',
            a: 'Nous travaillons avec Lendmark Financial Services pour des taux compétitifs. L\'approbation finale dépend de votre profil, du véhicule et du prêteur.',
          },
          calculator: {
            q: 'Puis-je estimer ma mensualité avant de postuler ?',
            a: 'Oui. Utilisez le calculateur sur la page Finance pour ajuster prix, apport, durée et TAEG et voir les paiements estimés.',
          },
          softCredit: {
            q: 'Vérifier mon taux affecte-t-il mon crédit ?',
            a: 'La préqualification utilise une enquête souple sans impact sur votre score. Une enquête dure peut survenir si vous poursuivez une demande complète.',
          },
        },
        selling: {
          instantOffer: {
            q: 'Comment obtenir une offre de vente ou reprise ?',
            a: 'Visitez Vente/Reprise et saisissez plaque, VIN ou détails. Nous proposons une offre compétitive à accepter ou appliquer à votre prochain achat.',
          },
          tradeInProcess: {
            q: 'Comment se déroule la reprise ?',
            a: 'Après l\'offre, apportez le véhicule pour vérification. Nous confirmons l\'état et les documents, puis appliquons la valeur à votre achat ou paiement.',
          },
          payDifference: {
            q: 'Puis-je reprendre et financer la différence ?',
            a: 'Oui. La valeur de reprise peut réduire le montant financé. Notre équipe aide à structurer un paiement adapté à votre budget.',
          },
        },
        delivery: {
          deliveryOptions: {
            q: 'Proposez-vous la livraison à domicile ?',
            a: 'La livraison peut être disponible selon votre lieu et le véhicule. Contactez-nous avec votre code postal pour options et devis.',
          },
          deliveryCost: {
            q: 'Combien coûte la livraison ?',
            a: 'Le tarif varie selon distance, véhicule et niveau de service. Certaines annonces incluent des offres promotionnelles — demandez sans engagement.',
          },
          pickup: {
            q: 'Puis-je récupérer en concession ?',
            a: 'Oui. Beaucoup de clients choisissent le retrait à Ocean Springs. Nous préparons les documents et vous accompagnons à la remise.',
          },
        },
        account: {
          createAccount: {
            q: 'Ai-je besoin d\'un compte pour acheter ?',
            a: 'Vous pouvez parcourir sans compte. Un compte gratuit permet de sauvegarder favoris, recherches, rendez-vous et progression financement.',
          },
          appointments: {
            q: 'Où gérer mes rendez-vous ?',
            a: 'Connectez-vous et ouvrez Rendez-vous dans votre menu pour consulter, reprogrammer ou annuler.',
          },
          wishlist: {
            q: 'Comment fonctionnent favoris et recherches sauvegardées ?',
            a: 'Utilisez le cœur pour sauvegarder des véhicules et Enregistrer la recherche sur Inventaire pour les alertes. Compte gratuit requis.',
          },
        },
        afterPurchase: {
          warranty: {
            q: 'Quelle garantie est disponible ?',
            a: 'La couverture varie selon le véhicule et peut inclure garantie constructeur restante ou extensions optionnelles. Demandez avant d\'acheter.',
          },
          returns: {
            q: 'Et si quelque chose ne va pas après l\'achat ?',
            a: 'Contactez-nous rapidement. Nous examinerons votre situation et chercherons une résolution équitable selon la loi et nos politiques.',
          },
          serviceHelp: {
            q: 'Qui contacter après l\'achat ?',
            a: 'Par téléphone, e-mail ou formulaire. Pour le financement, incluez les détails de votre demande pour une orientation rapide.',
          },
        },
      },
      support: {
        title: 'Besoin d\'aide ?',
        subtitle: 'Nos spécialistes peuvent vous guider — par téléphone, e-mail ou message.',
        callTitle: 'Appelez-nous',
        callDesc: '(228) 202-9614 · Lun–Sam',
        emailTitle: 'Écrivez-nous',
        emailDesc: 'sales@mycarslot.com',
        contactTitle: 'Envoyer un message',
        contactDesc: 'Réponse généralement sous un jour ouvré.',
        contactCta: 'Contactez-nous',
        inventoryCta: 'Parcourir l\'inventaire',
        financeCta: 'Explorer le financement',
        sellCta: 'Vendre ou reprendre',
      },
    },
    inventory: {
      compareCars: 'Comparer les véhicules',
      saveSearch: 'Enregistrer la recherche',
      filters: 'Filtres',
      sortBy: 'Trier par',
      noCars: 'Aucun véhicule ne correspond à vos critères.',
      clearFilters: 'Effacer les filtres',
      clearAll: 'Tout effacer',
      results: 'résultats',
      filter: 'Filtres',
      activeFiltersLabel: 'Filtres actifs :',
      saveSearchTitle: 'Enregistrer la recherche',
      share: 'Partager',
      linkCopied: 'Lien copié !',
      shareFilteredView: 'Partager la vue filtrée',
      copied: 'Copié !',
      comparingItems: 'Comparaison {{current}} sur {{max}}',
      removeFromComparison: 'Retirer de la comparaison',
      noCarsMatch: 'Aucune voiture ne correspond à vos filtres',
      tryClearingFilters: 'Essayez d\'effacer les filtres ou d\'ajuster les critères',
      totalCarsInDb: 'Total de voitures en base :',
      availableCars: 'Voitures disponibles :',
      pleaseLogInToSave: 'Connectez-vous pour enregistrer votre recherche',
    },
    finance: { title: 'Calculateur de financement', subtitle: 'Estimez votre mensualité ou le prix du véhicule.', ratesFrom: 'Taux à partir de', seeYourRate: 'Obtenez votre taux en quelques minutes.', yourCreditStaysUntouched: 'Votre score de crédit n\'est pas impacté.', checkYourRateSubline: 'Vérifiez votre taux en moins de 2 minutes. Sans impact sur votre crédit.', checkYourRate: 'Vérifier votre taux', paymentCalculator: 'Calculateur de mensualités', calcSubtitle: 'Estimez votre mensualité. Taux et conditions sous réserve d\'acceptation du crédit.', getPreQualified: 'Se préqualifier', applyPageTitle: 'Financez votre achat', applyPageLead: 'Passez à l\'étape suivante vers votre véhicule. Votre demande sécurisée ou le parcours partenaire pourra être intégré ici.', applyPagePlaceholder: 'Bientôt : intégration prêteur ou formulaire — votre équipe peut brancher un widget bancaire, un flux DMS ou une demande sur mesure.', backToFinanceCalculator: 'Retour au calculateur de mensualités' },
    sellTrade: {
      title: 'Vendre ou reprendre votre véhicule',
      getInstantOffer: 'Obtenir une offre',
      licensePlate: 'Plaque',
      vin: 'VIN',
      inStore: 'En magasin',
      heroTitle: 'Votre véhicule a de la valeur. Nous vous montrons combien.',
      heroSubtitle: 'Sans détour : un chiffre clair pour vendre ou reprendre.',
      howToGetOffer: 'Comment obtenir votre offre',
      receiveOfferInMinutes: 'Recevez votre offre estimée en quelques minutes',
      offerSuccessMessage: 'C\'est noté. Nous calculons et vous recontactons avec votre offre.',
      licensePlatePlaceholder: 'Numéro de plaque',
      selectState: 'Sélectionnez l\'État',
      zipCode: 'Code postal',
      zipCodePlaceholder: 'Code postal',
      getOffer: 'Obtenir une offre',
      runningNumbers: 'Calcul en cours…',
      vinPlaceholder: 'VIN (17 caractères)',
      preferStoreMessage: 'Vous préférez venir ? Passez nous voir et nous vous donnons un chiffre sur place, sans rendez-vous.',
      findOurLocation: 'Trouvez notre adresse',
      disclaimer: 'Les valeurs estimées sont soumises à une inspection et une évaluation finales en personne.',
      estimateTitle: 'Estimez la valeur de votre véhicule',
      estimateSubtitle: 'Estimation rapide. Offre finale après examen de votre véhicule.',
      year: 'Année',
      make: 'Marque',
      selectMake: 'Sélectionnez la marque',
      model: 'Modèle',
      selectModel: 'Sélectionnez le modèle',
      mileage: 'Kilométrage',
      condition: 'État',
      excellent: 'Excellent',
      good: 'Bon',
      fair: 'Correct',
      poor: 'Mauvais',
      estimatedValue: 'Valeur estimée',
      getYourOfferComingSoon: 'Obtenez votre offre (bientôt)',
      subjectToAppraisal: 'Soumis à évaluation en personne.',
      whySellTitle: 'Pourquoi vendre ou reprendre chez nous',
      whySellLead: 'Nous combinons des valorisations basées sur le marché et un processus simple pour une offre juste et une expérience fluide, que vous vendiez ou utilisiez la valeur pour votre prochain achat.',
      fairPricingTitle: 'Prix justes et transparents',
      fairPricingDesc: 'Votre offre repose sur les données du marché. Pas de sous-évaluation : un chiffre concurrentiel et fiable.',
      quickTurnaroundTitle: 'Traitement rapide',
      quickTurnaroundDesc: 'Recevez une offre préliminaire en minutes. De l\'évaluation au paiement, nous visons 24 h quand c\'est possible.',
      noObligationTitle: 'Sans engagement',
      noObligationDesc: 'Obtenez votre devis gratuit sans engagement. Vous décidez d\'accepter ou non ; pas de pression, pas de frais cachés.',
      tradeInValueTitle: 'Valeur de reprise',
      tradeInValueDesc: 'Utilisez la valeur de votre véhicule en crédit pour le prochain. Réduisez le financement et souvent la taxe.',
      professionalAppraisalTitle: 'Évaluation professionnelle',
      professionalAppraisalDesc: 'Une inspection brève et rigoureuse garantit que l\'offre finale reflète l\'état et la valeur marché de votre véhicule.',
      flexiblePaymentTitle: 'Options de paiement flexibles',
      flexiblePaymentDesc: 'Choisissez comment être payé : chèque, virement ou crédit de reprise. Paiement le jour même possible.',
      howItWorksTitle: 'Comment fonctionne la vente ou la reprise',
      howItWorksLead: 'De la première estimation au paiement ou au crédit de reprise en trois étapes.',
      step1Title: 'Obtenez votre offre préliminaire',
      step1Desc: 'Saisissez les infos de votre véhicule (plaque, VIN) ou venez nous voir. Nous vous donnons une offre préliminaire selon les détails fournis.',
      step2Title: 'Planifiez votre évaluation',
      step2Desc: 'Réservez une évaluation gratuite et sans engagement. Nous pouvons nous déplacer ou vous venez. L\'évaluation prend environ 30 minutes.',
      step3Title: 'Acceptez votre offre et soyez payé',
      step3Desc: 'Une fois l\'offre finale acceptée, nous gérons les formalités. Paiement le jour même par chèque ou virement, ou en crédit de reprise.',
      whatSellersSay: 'Ce que disent nos vendeurs',
      joinThousands: 'Rejoignez des milliers de clients satisfaits',
      testimonial1: '"J\'ai vendu ma voiture en moins de 24 h ! Processus très fluide et prix meilleur qu\'ailleurs."',
      testimonial2: '"J\'ai utilisé ma reprise pour une nouvelle voiture. MyCarSlot a tout simplifié. Je recommande !"',
      testimonial3: '"Pas de marchandage, pas de stress. Prix juste et paiement immédiat. La vente auto devrait toujours être comme ça !"',
      errorLicensePlate: 'Entrez le numéro de plaque',
      errorSelectState: 'Sélectionnez votre État',
      errorVin: 'Entrez le VIN du véhicule',
      errorVinLength: 'Le VIN doit faire {{length}} caractères',
      errorZipRequired: 'Le code postal est requis',
      errorZipInvalid: 'Entrez un code postal à 5 ou 9 chiffres valide',
    },
  },
  de: {
    home: {
      heroFindTitle: 'Finden Sie Ihr Auto',
      price: 'Preis',
      brand: 'Marke',
      type: 'Typ',
      year: 'Jahr',
      any: 'Alle',
      search: 'Suchen',
      heroTitle: 'Ehrliche Autos.',
      heroTitleAccent: 'Klare Geschäfte.',
      heroSlide2Title: 'Qualität, der Sie vertrauen können.',
      heroSlide2Accent: 'Jedes Mal.',
      heroSlide3Title: 'Transparente Preise.',
      heroSlide3Accent: 'Keine Überraschungen.',
      heroTrust1: 'Finanzierung verfügbar',
      heroTrust2: 'Vollständige Fahrzeughistorie',
      heroTrust3: 'Faire Inzahlungnahme',
      heroSubtitle: 'Qualitätsgeprüfte Gebrauchtwagen, transparente Preise und Service, dem Sie vertrauen können. Entdecken Sie {{count}}+ Fahrzeuge von {{brands}} Marken.',
      browseInventory: 'Bestand durchsuchen',
      soundOn: 'Hintergrundmusik einschalten',
      soundOff: 'Hintergrundmusik ausschalten',
      contactUs: 'Kontakt',
      trustedDealer: 'Vertrauenshändler',
      licensedTransparent: 'Lizenziert und transparent',
      awardWinningService: 'Vertrauenswürdiger Service',
      customerFirst: 'Kundenerlebnis im Fokus',
      transparentPricing: 'Transparente Preise',
      noHiddenFees: 'Keine versteckten Gebühren',
      responsibleBuying: 'Verantwortungsvoller Kauf',
      qualityPreOwned: 'Qualitätsgeprüfte Gebrauchtwagen',
      newArrivalsThisWeek: 'Neu diese Woche',
      vehiclesJustAdded: '{{count}}+ Fahrzeuge neu eingetroffen',
      findOutMore: 'Mehr erfahren',
      financingAvailable: 'Finanzierung verfügbar',
      getPreApproved: 'In Minuten vorab genehmigt',
      sellOrTrade: 'Verkaufen oder in Zahlung geben',
      instantValuation: 'Sofortbewertung, faire Preise',
      whatWeOffer: 'Was wir bieten',
      fromBrowsingToFinancing: 'Vom Stöbern bis zur Finanzierung – wir begleiten Sie in jedem Schritt.',
      seeMore: 'Mehr sehen',
      browseInventoryDesc: 'Entdecken Sie unsere Auswahl an qualitätsgeprüften Gebrauchtwagen. Jedes Auto ist geprüft und bereit für Ihre nächste Fahrt.',
      sellTradeTitle: 'Verkauf / Inzahlungnahme',
      sellTradeDesc: 'Sofortbewertung für Ihr Fahrzeug. Wechseln Sie mit einem einfachen, transparenten Prozess zu Ihrem nächsten Auto.',
      financingTitle: 'Finanzierung',
      financingDesc: 'Wettbewerbsfähige Konditionen und flexible Laufzeiten. Vorabgenehmigung in Minuten.',
      contactSupport: 'Kontakt & Support',
      contactSupportDesc: 'Besuchen Sie uns, buchen Sie eine Probefahrt oder stellen Sie Fragen. Wir helfen Ihnen, das richtige Auto zu finden.',
      carsAvailable: 'Autos verfügbar',
      carsSold: 'Autos verkauft',
      newArrivals: 'Neu eingetroffen',
      brands: 'Marken',
      mostViewedCars: 'Meist angesehene Fahrzeuge',
      shopByType: 'Nach Typ einkaufen',
      popularBrands: 'Beliebte Marken',
      trustedManufacturers: 'Vertrauenswürdige Hersteller',
      available: 'verfügbar',
      comingSoon: 'Demnächst',
      financingOptions: 'Finanzierungsoptionen',
      preApprovalTerms: 'Vorabgenehmigung in Minuten, wettbewerbsfähige Konditionen, flexible Pläne.',
      quickApproval: 'Schnelle Genehmigung',
      competitiveRates: 'Wettbewerbsfähige Konditionen',
      flexiblePlans: 'Flexible Pläne',
      tradeInService: 'Inzahlungnahme-Service',
      tradeInDesc: 'Sofortbewertung und faire Marktpreise für Ihr aktuelles Fahrzeug.',
      instantValuationLabel: 'Sofortbewertung',
      fairPricing: 'Faire Preise',
      simpleProcess: 'Einfacher Ablauf',
    },
    common: {
      inventory: 'Bestand',
      finance: 'Finanzierung',
      sellTrade: 'Verkauf/Inzahlungnahme',
      contact: 'Kontakt',
      signIn: 'Anmelden',
      saved: 'Gespeichert',
      searchPlaceholder: 'Welches Auto suchen Sie?',
      prev: 'Zurück',
      next: 'Weiter',
      goToSlide: 'Zu Folie',
      skipToContent: 'Zum Hauptinhalt springen',
      backToTop: 'Nach oben',
    },
    profile: {
      signInToView: 'Melden Sie sich an, um Ihr Profil zu sehen',
      personalInfo: 'Persönliche Daten',
      paymentMethods: 'Zahlungsmethoden',
      documents: 'Dokumente',
      security: 'Sicherheit',
      notifications: 'Benachrichtigungen',
      preferences: 'Einstellungen',
      personalInformation: 'Persönliche Informationen',
      paymentMethodsTitle: 'Zahlungsmethoden',
      documentsTitle: 'Dokumente',
      securitySettings: 'Sicherheitseinstellungen',
      preferencesTitle: 'Einstellungen',
      customizeExperience: 'Passen Sie Ihre Erfahrung an',
      loadingPreferences: 'Einstellungen werden geladen...',
      appearance: 'Darstellung',
      theme: 'Design',
      light: 'Hell',
      whiteBackground: 'Weißer Hintergrund',
      dark: 'Dunkel',
      darkBackground: 'Dunkler Hintergrund',
      languageAndRegion: 'Sprache und Region',
      language: 'Sprache',
      timezone: 'Zeitzone',
      dateFormat: 'Datumsformat',
      currency: 'Währung',
      uploadDocuments: 'Dokumente hochladen',
      yourDocuments: 'Ihre Dokumente',
      chooseFiles: 'Dateien auswählen',
      uploading: 'Wird hochgeladen...',
      person: 'Person',
      addCard: 'Karte hinzufügen',
      adding: 'Wird hinzugefügt...',
      save: 'Speichern',
      cancel: 'Abbrechen',
      remove: 'Entfernen',
    },
    footer: {
      companyDesc: 'Wir bieten außergewöhnliche Automobillösungen durch unser Angebot an geprüften Gebrauchtwagen, transparente Preise und erstklassigen Kundenservice.',
      shop: 'Shop',
      browseInventory: 'Bestand durchsuchen',
      financingOptions: 'Finanzierungsoptionen',
      sellYourCar: 'Auto verkaufen',
      tradeInValue: 'Inzahlungnahmewert',
      company: 'Unternehmen',
      aboutUs: 'Über uns',
      contact: 'Kontakt',
      careers: 'Karriere',
      blog: 'Blog',
      support: 'Support',
      faq: 'FAQ',
      shippingInfo: 'Versand',
      warranty: 'Garantie',
      returns: 'Rückgaben',
      customerFeedbackNps: 'Kundenfeedback',
      purchaseExperienceSurvey: 'Funktionen & Verbesserungen vorschlagen',
      legal: 'Rechtliches',
      privacyPolicy: 'Datenschutz',
      termsOfService: 'Nutzungsbedingungen',
      cookiesPolicy: 'Cookie-Richtlinie',
      allRightsReserved: 'Alle Rechte vorbehalten.',
    },
    auth: {
      welcome: 'Willkommen',
      enterEmailToStart: 'E-Mail eingeben, um zu starten',
      email: 'E-Mail',
      emailPlaceholder: 'ihre@email.com',
      continue: 'Weiter',
      checking: 'Wird geprüft...',
      createAccount: 'Konto erstellen',
      completeProfile: 'Profil vervollständigen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      phone: 'Telefon',
      password: 'Passwort',
      createPassword: 'Passwort erstellen',
      minChars: '8+ Zeichen',
      number: 'Zahl',
      letter: 'Buchstabe',
      noSpaces: 'Keine Leerzeichen',
      createAccountButton: 'Konto erstellen',
      creating: 'Wird erstellt...',
      welcomeBack: 'Willkommen zurück',
      signInToAccount: 'Bei Ihrem Konto anmelden',
      noAccount: 'Noch kein Konto?',
      createAnAccount: 'Konto erstellen',
      enterPassword: 'Passwort eingeben',
      keepSignedIn: 'Angemeldet bleiben',
      forgotPassword: 'Passwort vergessen?',
      twoFactorCode: 'Zwei-Faktor-Authentifizierungscode',
      enter6DigitCode: '6-stelligen Code eingeben',
      enterCodeFromApp: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein',
      verify: 'Überprüfen',
      signingIn: 'Anmeldung...',
      verifying: 'Überprüfung...',
      resetPassword: 'Passwort zurücksetzen',
      sendResetCode: 'Wir senden Ihnen einen Code',
      sendResetCodeButton: 'Code senden',
      sending: 'Wird gesendet...',
      newPassword: 'Neues Passwort',
      enterCodeAndPassword: 'Code und neues Passwort eingeben',
      resetCode: 'Zurücksetzungscode',
      resetCodePlaceholder: '6-stelliger Code',
      confirmPassword: 'Passwort bestätigen',
      confirmPasswordPlaceholder: 'Passwort bestätigen',
      resetPasswordButton: 'Passwort zurücksetzen',
      resetting: 'Wird zurückgesetzt...',
    },
    carCard: {
      used: 'GEBRAUCHT',
      certified: 'ZERTIFIZIERT',
      estimatedPrice: 'Geschätzter Preis',
      priceTbd: 'Preis n. b.',
      estimatedMo: 'Geschätzt',
      cashDown: '0 € Anzahlung',
      freeDelivery: 'Kostenlose Lieferung',
      shipping: 'Versand',
      deliveredBefore: 'Geliefert vor',
      calculatingShipping: 'Versand wird berechnet...',
      compare: 'Vergleichen',
      addToWishlist: 'Zu Favoriten',
      expectedLaunch: 'ERWARTETER START:',
      priceDrop: 'Preissenkung',
      was: 'war',
      vehicle: 'Fahrzeug',
      prequalCta: 'Vorqualifizieren Sie sich, um Ihre persönliche monatliche Rate zu sehen',
    },
    carDetails: {
      priceHistoryTitle: 'Preisverlauf',
      priceWentDown: 'Preis gesenkt um',
      priceWentUp: 'Preis erhöht um',
    },
    cookie: {
      weUseCookies: 'Wir verwenden Cookies',
      cookieMessage: 'Wir verwenden Cookies, um Ihre Nutzung zu verbessern, den Verkehr zu analysieren und Inhalte zu personalisieren. Mit "Alle akzeptieren" stimmen Sie der Verwendung zu.',
      customize: 'Anpassen',
      rejectAll: 'Alle ablehnen',
      acceptAll: 'Alle akzeptieren',
      cookiePreferences: 'Cookie-Einstellungen',
      managePreferences: 'Verwalten Sie Ihre Cookie-Einstellungen.',
      essentialCookies: 'Essenzielle Cookies',
      essentialDesc: 'Für die Funktion der Website erforderlich. Können nicht deaktiviert werden.',
      analyticsCookies: 'Analyse-Cookies',
      analyticsDesc: 'Helfen uns zu verstehen, wie Besucher die Website nutzen.',
      marketingCookies: 'Marketing-Cookies',
      marketingDesc: 'Für personalisierte Werbung und Kampagnenauswertung.',
      dataSharing: 'Datenweitergabe',
      dataSharingDesc: 'Erlauben Sie uns, Ihre Daten mit vertrauenswürdigen Partnern zu teilen.',
      savePreferences: 'Einstellungen speichern',
      saving: 'Wird gespeichert...',
    },
    notFound: {
      carNotFound: 'Auto nicht gefunden',
      carNotFoundMessage: 'Das gesuchte Auto existiert nicht oder wurde entfernt.',
      backToInventory: 'Zurück zum Bestand',
      pageNotFound: 'Seite nicht gefunden',
      pageNotFoundMessage: 'Die gesuchte Seite existiert nicht.',
      goHome: 'Startseite',
      goBackToCar: 'Zurück zur Fahrzeugseite',
    },
    contact: {
      pageEyebrow: 'Support',
      getInTouch: 'Kontakt',
      getInTouchSub: 'Fragen zu Bestand, Finanzierung oder Lieferung? Wählen Sie die schnellste Option — oder senden Sie uns eine Nachricht während der Öffnungszeiten.',
      quickActionsTitle: 'So erreichen Sie uns',
      chatAction: 'Live-Chat',
      chatActionDesc: 'Schnelle Antworten beim Stöbern',
      phoneAction: 'Anrufen',
      faceTimeAction: 'FaceTime',
      faceTimeActionDesc: 'Apple-Geräte · Öffnungszeiten',
      emailAction: 'E-Mail',
      emailActionDesc: 'sales@mycarslot.com',
      faqAction: 'FAQ & Hilfe',
      faqActionDesc: 'Häufige Fragen',
      formLead: 'Wir antworten in der Regel am selben Werktag während der Öffnungszeiten.',
      directions: 'Route planen',
      contactInfo: 'Kontaktdaten',
      contactInfoText: 'Spontane Besuche willkommen. Buchen Sie online eine Probefahrt — wir bereiten Ihr Fahrzeug vor.',
      address: 'Adresse',
      phone: 'Telefon',
      email: 'E-Mail',
      hours: 'Öffnungszeiten',
      sendMessage: 'Nachricht senden',
      name: 'Name',
      subject: 'Betreff',
      message: 'Nachricht',
      send: 'Senden',
      sending: 'Wird gesendet...',
      successMessage: 'Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.',
      businessHours: 'Geschäftszeiten',
      fullName: 'Vollständiger Name',
      selectSubject: 'Betreff auswählen',
      generalInquiry: 'Allgemeine Anfrage',
      salesQuestion: 'Verkaufsfrage',
      serviceSupport: 'Service & Support',
      tradeIn: 'Inzahlungnahme',
      other: 'Sonstiges',
      messagePlaceholder: 'Teilen Sie uns mit, wie wir helfen können...',
      sendButton: 'Nachricht senden',
      visitDealership: 'Besuchen Sie uns',
    },
    faqSupport: {
      eyebrow: 'Hilfecenter',
      heroTitle: 'FAQ & Support',
      heroSubtitle: 'Antworten zu Kauf, Finanzierung, Verkauf, Lieferung und Ihrem Konto — oder kontaktieren Sie unser Team.',
      hubSubtitle: 'Wählen Sie ein Thema für häufige Fragen oder kontaktieren Sie unser Team.',
      helpCenter: 'Hilfecenter',
      searchPlaceholder: 'Fragen suchen...',
      topicsLabel: 'FAQ-Themen',
      browseByTopic: 'Nach Thema durchsuchen',
      popularLabel: 'Beliebte Fragen',
      quickLinksLabel: 'Schnelllinks',
      hoursTitle: 'Support-Zeiten',
      hoursWeekday: 'Mo–Fr: 9:00 – 19:00 Uhr',
      hoursSaturday: 'Sa: 9:00 – 18:00 Uhr · So: 10:00 – 17:00 Uhr',
      allTopics: 'Alle Themen',
      resultCount: '{{count}} Fragen',
      noResults: 'Keine passenden Fragen gefunden',
      noResultsHint: 'Versuchen Sie einen anderen Begriff oder kontaktieren Sie uns.',
      backToTopic: 'Zurück zu {{topic}}',
      moreInTopic: 'Mehr in {{topic}}',
      expertTitle: 'Sprechen Sie mit einem Experten',
      expertMessage: 'Senden Sie uns eine Nachricht — wir helfen beim richtigen Auto und bei Fragen.',
      expertPhone: 'Rufen Sie (228) 202-9614 an für Kauf, Verkauf oder Lieferung.',
      categoryDesc: {
        buying: 'Bestand, Auction to Own, Historie und Qualitätsstandards',
        finding: 'Ablauf, Budget, Vergleich und gespeicherte Suchen',
        financing: 'Vorqualifizierung, Lendmark, Raten und Bonität',
        payments: 'Fälligkeit, Zahlungsarten, vorzeitige Tilgung',
        selling: 'Verkaufsangebote, Inzahlungnahme und Wertanrechnung',
        delivery: 'Hauslieferung, Abholung und Versandangebote',
        storeVisit: 'Probefahrten, Termine und mitzubringende Unterlagen',
        warranty: 'Werksgarantie, Erweiterungen und Deckung',
        sourcing: 'Auction to Own, Sourcing-Gebühr und Zeitrahmen',
        account: 'Merkliste, gespeicherte Suchen und Termine',
        security: 'Kontosicherheit, Datenschutz und Betrug',
        recalls: 'Offene Rückrufe, Reparaturen und Prüfung',
        afterPurchase: 'Rückgaben, Dokumente und Support nach dem Kauf',
      },
      categories: {
        buying: 'Auto kaufen',
        finding: 'Auto finden',
        financing: 'Finanzierung',
        payments: 'Zahlungen leisten',
        selling: 'Verkauf & Inzahlung',
        delivery: 'Lieferung & Abholung',
        storeVisit: 'Besuche & Probefahrten',
        warranty: 'Garantien & Schutz',
        sourcing: 'Auction to Own & Sourcing',
        account: 'Ihr Konto',
        security: 'Sicherheit & Datenschutz',
        recalls: 'Sicherheitsrückrufe',
        afterPurchase: 'Nach dem Kauf',
      },
      questions: {
        buying: {
          browseInventory: {
            q: 'Wie durchsuche und vergleiche ich Fahrzeuge online?',
            a: 'Öffnen Sie Bestand, um nach Marke, Modell, Preis, Typ und Laufleistung zu filtern. Speichern Sie Favoriten, vergleichen Sie Angebote und vereinbaren Sie einen Besuch.',
          },
          testDrive: {
            q: 'Kann ich eine Probefahrt planen?',
            a: 'Ja. Wählen Sie ein Fahrzeug und buchen Sie online, oder kontaktieren Sie uns für einen Termin. Spontane Besuche sind je nach Verfügbarkeit möglich.',
          },
          auctionToOwn: {
            q: 'Was ist das Auction to Own Programm?',
            a: 'Nennen Sie Marke, Modell und Budget — wir beschaffen das Fahrzeug über unsere Auktionsnetzwerk für eine Pauschale unter 150 $. Sie behalten die Ersparnis.',
          },
          vehicleHistory: {
            q: 'Enthalten Fahrzeuge Historie und Inspektion?',
            a: 'Wir bieten transparente Angaben und erläutern Historieberichte, Zustand und Inspektion für jedes Fahrzeug.',
          },
        },
        financing: {
          preQual: {
            q: 'Wie funktioniert die Vorqualifizierung?',
            a: 'Nutzen Sie Meinen Zinssatz prüfen auf Finanzierung oder Get Financed. Die Vorqualifizierung ist schnell und liefert geschätzte Konditionen zum sicheren Einkaufen.',
          },
          lendmark: {
            q: 'Wer bietet Autofinanzierung an?',
            a: 'Wir arbeiten mit Lendmark Financial Services für wettbewerbsfähige Konditionen. Die endgültige Genehmigung hängt von Profil, Fahrzeug und Kreditgeber ab.',
          },
          calculator: {
            q: 'Kann ich die Rate vor der Bewerbung schätzen?',
            a: 'Ja. Nutzen Sie den Rechner auf der Finanzierungsseite für Preis, Anzahlung, Laufzeit und APR in Echtzeit.',
          },
          softCredit: {
            q: 'Beeinflusst die Zinsprüfung meine Bonität?',
            a: 'Die Vorqualifizierung nutzt eine weiche Anfrage ohne Score-Auswirkung. Eine harte Anfrage kann bei vollständiger Bewerbung erfolgen.',
          },
        },
        selling: {
          instantOffer: {
            q: 'Wie erhalte ich ein Verkaufs- oder Inzahlungsangebot?',
            a: 'Besuchen Sie Verkauf/Inzahlung und geben Sie Kennzeichen, FIN oder Details ein. Wir machen ein wettbewerbsfähiges Angebot zum Annehmen oder Verrechnen.',
          },
          tradeInProcess: {
            q: 'Wie läuft die Inzahlung ab?',
            a: 'Nach dem Angebot bringen Sie das Fahrzeug zur Prüfung. Wir bestätigen Zustand und Unterlagen und verrechnen den Wert mit Ihrem Kauf oder zahlen aus.',
          },
          payDifference: {
            q: 'Kann ich inzahlung nehmen und die Differenz finanzieren?',
            a: 'Ja. Der Inzahlungswert kann den Finanzierungsbetrag senken. Unser Team hilft bei einer passenden Rate.',
          },
        },
        delivery: {
          deliveryOptions: {
            q: 'Bieten Sie Hauslieferung an?',
            a: 'Lieferung kann je nach Standort und Fahrzeug möglich sein. Kontaktieren Sie uns mit Ihrer PLZ für Optionen und Angebot.',
          },
          deliveryCost: {
            q: 'Was kostet die Lieferung?',
            a: 'Der Preis hängt von Entfernung, Fahrzeug und Service ab. Manche Angebote enthalten Aktionen — fragen Sie unverbindlich.',
          },
          pickup: {
            q: 'Kann ich im Autohaus abholen?',
            a: 'Ja. Viele Kunden holen in Ocean Springs ab. Wir bereiten Unterlagen vor und begleiten die Übergabe.',
          },
        },
        account: {
          createAccount: {
            q: 'Brauche ich ein Konto zum Einkaufen?',
            a: 'Sie können ohne Konto stöbern. Ein kostenloses Konto speichert Favoriten, Suchen, Termine und Finanzierungsfortschritt.',
          },
          appointments: {
            q: 'Wo verwalte ich Termine?',
            a: 'Melden Sie sich an und öffnen Sie Termine im Kontomenü zum Ansehen, Verschieben oder Stornieren.',
          },
          wishlist: {
            q: 'Wie funktionieren Favoriten und gespeicherte Suchen?',
            a: 'Nutzen Sie das Herz für Favoriten und Suche speichern im Bestand für Benachrichtigungen. Kostenloses Konto erforderlich.',
          },
        },
        afterPurchase: {
          warranty: {
            q: 'Welche Garantie ist verfügbar?',
            a: 'Die Abdeckung variiert und kann Restwerksgarantie oder optionale Erweiterungen umfassen. Fragen Sie vor dem Kauf.',
          },
          returns: {
            q: 'Was, wenn nach dem Kauf etwas nicht stimmt?',
            a: 'Kontaktieren Sie uns umgehend. Wir prüfen Ihre Situation und suchen eine faire Lösung nach Gesetz und Richtlinien.',
          },
          serviceHelp: {
            q: 'Wen kontaktiere ich nach dem Kauf?',
            a: 'Per Telefon, E-Mail oder Kontaktformular. Für Finanzierung geben Sie Bewerbungsdetails für schnelle Hilfe an.',
          },
        },
      },
      support: {
        title: 'Noch Fragen?',
        subtitle: 'Unsere Spezialisten helfen per Telefon, E-Mail oder Nachricht.',
        callTitle: 'Anrufen',
        callDesc: '(228) 202-9614 · Mo–Sa',
        emailTitle: 'E-Mail',
        emailDesc: 'sales@mycarslot.com',
        contactTitle: 'Nachricht senden',
        contactDesc: 'Antwort meist innerhalb eines Werktags.',
        contactCta: 'Kontakt',
        inventoryCta: 'Bestand ansehen',
        financeCta: 'Finanzierung entdecken',
        sellCta: 'Verkaufen oder inzahlung nehmen',
      },
    },
    inventory: {
      compareCars: 'Fahrzeuge vergleichen',
      saveSearch: 'Suche speichern',
      filters: 'Filter',
      sortBy: 'Sortieren nach',
      noCars: 'Keine Fahrzeuge entsprechen Ihren Filtern.',
      clearFilters: 'Filter zurücksetzen',
      clearAll: 'Alle löschen',
      results: 'Ergebnisse',
      filter: 'Filter',
      activeFiltersLabel: 'Aktive Filter:',
      saveSearchTitle: 'Suche speichern',
      share: 'Teilen',
      linkCopied: 'Link kopiert!',
      shareFilteredView: 'Gefilterte Ansicht teilen',
      copied: 'Kopiert!',
      comparingItems: 'Vergleich {{current}} von {{max}}',
      removeFromComparison: 'Aus Vergleich entfernen',
      noCarsMatch: 'Keine Fahrzeuge entsprechen Ihren Filtern',
      tryClearingFilters: 'Filter zurücksetzen oder Suchkriterien anpassen',
      totalCarsInDb: 'Fahrzeuge gesamt:',
      availableCars: 'Verfügbare Fahrzeuge:',
      pleaseLogInToSave: 'Bitte anmelden, um die Suche zu speichern',
    },
    finance: { title: 'Finanzierungsrechner', subtitle: 'Schätzen Sie Ihre monatliche Rate oder den Fahrzeugpreis.', ratesFrom: 'Raten ab', seeYourRate: 'Sehen Sie in Minuten Ihren Zinssatz.', yourCreditStaysUntouched: 'Ihre Bonität bleibt unberührt.', checkYourRateSubline: 'Prüfen Sie in unter 2 Minuten Ihren Zinssatz. Keine Auswirkung auf Ihre Bonität.', checkYourRate: 'Zinssatz prüfen', paymentCalculator: 'Ratenrechner', calcSubtitle: 'Schätzen Sie Ihre monatliche Rate. Zinsen und Laufzeiten abhängig von der Kreditgenehmigung.', getPreQualified: 'Vorab qualifizieren', applyPageTitle: 'Finanzierung sichern', applyPageLead: 'Der nächste Schritt zu Ihrem Fahrzeug. Hier kann Ihr sicherer Antrag oder Partner-Flow eingebunden werden.', applyPagePlaceholder: 'Demnächst: Kreditgeber-Anbindung oder Formular — Ihr Team kann Bank-Widget, DMS-Flow oder einen eigenen Antrag einbinden.', backToFinanceCalculator: 'Zurück zum Ratenrechner' },
    sellTrade: {
      title: 'Auto verkaufen oder in Zahlung geben',
      getInstantOffer: 'Sofortangebot',
      licensePlate: 'Kennzeichen',
      vin: 'FIN',
      inStore: 'Vor Ort',
      heroTitle: 'Ihr Auto hat einen Wert. Wir zeigen Ihnen, wie viel.',
      heroSubtitle: 'Ohne Umwege – eine klare Zahl zum Verkaufen oder Inzahlunggeben.',
      howToGetOffer: 'So erhalten Sie Ihr Angebot',
      receiveOfferInMinutes: 'Erhalten Sie in wenigen Minuten Ihr geschätztes Angebot',
      offerSuccessMessage: 'Verstanden. Wir rechnen und melden uns mit Ihrem Angebot.',
      licensePlatePlaceholder: 'Kennzeichen',
      selectState: 'Bundesland wählen',
      zipCode: 'PLZ',
      zipCodePlaceholder: 'PLZ',
      getOffer: 'Angebot erhalten',
      runningNumbers: 'Wir berechnen…',
      vinPlaceholder: 'FIN (17 Zeichen)',
      preferStoreMessage: 'Sie möchten lieber vorbeikommen? Kommen Sie vorbei – wir nennen Ihnen vor Ort einen Betrag, ohne Termin.',
      findOurLocation: 'Standort finden',
      disclaimer: 'Geschätzte Werte unterliegen der Besichtigung und finalen Bewertung vor Ort.',
      estimateTitle: 'Schätzen Sie den Wert Ihres Autos',
      estimateSubtitle: 'Schnellschätzung. Endgültiges Angebot nach Prüfung Ihres Fahrzeugs.',
      year: 'Jahr',
      make: 'Marke',
      selectMake: 'Marke wählen',
      model: 'Modell',
      selectModel: 'Modell wählen',
      mileage: 'Kilometerstand',
      condition: 'Zustand',
      excellent: 'Ausgezeichnet',
      good: 'Gut',
      fair: 'Mittel',
      poor: 'Schlecht',
      estimatedValue: 'Geschätzter Wert',
      getYourOfferComingSoon: 'Ihr Angebot (demnächst)',
      subjectToAppraisal: 'Vor-Ort-Bewertung vorbehalten.',
      whySellTitle: 'Warum bei uns verkaufen oder in Zahlung geben',
      whySellLead: 'Wir verbinden marktbasierte Bewertungen mit einem klaren Ablauf – faires Angebot und reibungsloser Ablauf, ob Verkauf oder Anrechnung auf Ihr nächstes Auto.',
      fairPricingTitle: 'Faire, transparente Preise',
      fairPricingDesc: 'Ihr Angebot basiert auf aktuellen Marktdaten. Kein Unterbieten – Sie erhalten eine vertrauenswürdige, wettbewerbsfähige Zahl.',
      quickTurnaroundTitle: 'Schnelle Abwicklung',
      quickTurnaroundDesc: 'Erhalten Sie in Minuten ein Vorangebot. Unser Ziel: von der Bewertung bis zur Zahlung innerhalb von 24 Stunden, wenn möglich.',
      noObligationTitle: 'Unverbindlich',
      noObligationDesc: 'Kostenloses Angebot ohne Verpflichtung. Sie entscheiden – kein Druck, keine versteckten Gebühren.',
      tradeInValueTitle: 'Inzahlungnahmewert',
      tradeInValueDesc: 'Nutzen Sie den Wert Ihres Fahrzeugs als Anzahlung für das nächste Auto. Weniger Finanzierung, oft weniger Umsatzsteuer.',
      professionalAppraisalTitle: 'Professionelle Bewertung',
      professionalAppraisalDesc: 'Eine kurze, gründliche Prüfung stellt sicher, dass Ihr Endangebot Zustand und Marktwert Ihres Fahrzeugs widerspiegelt.',
      flexiblePaymentTitle: 'Flexible Zahlungsoptionen',
      flexiblePaymentDesc: 'Wählen Sie: Scheck, Überweisung oder Inzahlungnahme für Ihren nächsten Kauf. Zahlung am selben Tag möglich.',
      howItWorksTitle: 'So funktioniert Verkauf oder Inzahlungnahme',
      howItWorksLead: 'Vom ersten Angebot bis zur Zahlung oder Anrechnung in drei Schritten.',
      step1Title: 'Vorangebot erhalten',
      step1Desc: 'Geben Sie oben Ihre Fahrzeugdaten ein (Kennzeichen, FIN) oder besuchen Sie uns. Wir erstellen ein Vorangebot anhand Ihrer Angaben.',
      step2Title: 'Bewertungstermin vereinbaren',
      step2Desc: 'Kostenlose, unverbindliche Bewertung nach Ihren Wünschen. Wir kommen zu Ihnen oder Sie zu uns. Die Bewertung dauert etwa 30 Minuten.',
      step3Title: 'Angebot annehmen und bezahlt werden',
      step3Desc: 'Nach Annahme unseres Endangebots erledigen wir die Formalitäten. Bezahlung am selben Tag per Scheck oder Überweisung oder als Anrechnung.',
      whatSellersSay: 'Was unsere Verkäufer sagen',
      joinThousands: 'Tausende zufriedene Kunden',
      testimonial1: '"Auto in unter 24 Stunden verkauft! Ablauf super unkompliziert, Preis besser als bei anderen Händlern."',
      testimonial2: '"Inzahlungnahme für ein neues Auto genutzt. MyCarSlot hat alles reibungslos gemacht. Sehr empfehlenswert!"',
      testimonial3: '"Kein Feilschen, kein Stress. Faire Preis, sofortige Zahlung. So sollte Autoverkauf sein!"',
      errorLicensePlate: 'Kennzeichen eingeben',
      errorSelectState: 'Bundesland wählen',
      errorVin: 'Fahrzeug-FIN eingeben',
      errorVinLength: 'FIN muss {{length}} Zeichen haben',
      errorZipRequired: 'PLZ ist erforderlich',
      errorZipInvalid: 'Gültige 5- oder 9-stellige PLZ eingeben',
    },
  },
};

// Fallback: use English for any missing language
const fallback = translations.en;

function getLang(locale) {
  if (!locale || typeof locale !== 'string') return 'en';
  const code = locale.split('-')[0];
  return translations[code] ? code : 'en';
}

/**
 * Get translated string. Key is dot path e.g. 'home.heroTitle'.
 * Replace {{count}}, {{brands}} etc. with opts.
 */
export function t(locale, key, opts = {}) {
  const lang = getLang(locale);
  const obj = translations[lang] || fallback;
  const keys = key.split('.');
  let value = obj;
  for (const k of keys) {
    value = value?.[k];
  }
  if (typeof value !== 'string') {
    const fallbackVal = fallback;
    let v = fallbackVal;
    for (const k of keys) v = v?.[k];
    value = typeof v === 'string' ? v : key;
  }
  return Object.keys(opts).reduce((s, k) => s.replace(new RegExp(`{{${k}}}`, 'g'), opts[k]), value);
}

export default translations;
